"use client";

import { useParams, useRouter, useSearchParams } from "next/navigation";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocale } from "@/hooks/useLocale";
import dynamic from "next/dynamic";
import { FiLock } from "react-icons/fi";
import { BookOpenTextIcon } from "@/app/components/icons/BookOpenTextIcon";
import { useTradingModalStore } from "@/stores/useTradingModalStore";
import DextoolsLandingBar from "@/app/components/DextoolsLandingBar";
import { useDextoolsSignupStore } from "@/stores/useDextoolsSignupStore";
import { detectAndStoreDextoolsUtm } from "@/lib/dextools-tracking";
import { getCountryFlagUrl } from "@/lib/country-data";
import { useT } from "@/hooks/useT";
import { prefetchPriceHistory } from "@/app/components/EventMultiOutcomeChart";
import { useNetworkActivity, getTradersForMarket } from "@/app/components/NetworkBetsSection";
import { useAIContextAnalysis } from "@/hooks/useAIContextAnalysis";
import { useRealtimeMarketPrice, type MarketLike } from "@/hooks/useRealtimeMarketPrice";
import { useDFlowRealtimePrice } from "@/hooks/useDFlowRealtimePrice";
import { useKalshiLiveTickers } from "@/hooks/useKalshiLiveTickers";
import { usePredictRealtimePrice } from "@/hooks/usePredictRealtimePrice";
import { useHyperliquidRealtimePrice } from "@/hooks/useHyperliquidRealtimePrice";
import { useParallaxScroll } from "./hooks/useParallaxScroll";
import { useDwellTracking } from "./hooks/useDwellTracking";
import { EventHeroChart } from "./components/EventHeroChart";
import { useProofKYC } from "@/hooks/useProofKYC";
import { sourceUrl, type Source } from "@/lib/source-url";
import { useEventQuery, type EventData } from "@/hooks/useEventQuery";
import { useEventWidgetConfig } from "@/hooks/useEventWidgetConfig";
// 8 components dynamic'd below the fold / mobile-only — Chrome DevTools
// traced an INP of 4193 ms on a card click into /event/[slug] cold load.
// Cause: every below-fold widget (MarketTalks comments thread = 1177 lines,
// MarketRelatedArticles, ResolutionDetails, KalshiRelatedMarketsCard,
// PredictTweetTracker) plus three mobile-only sheets (MobileBottomSheet,
// MobileMarketDetailOverlay, MobileEventStickyHeader) shipped synchronously
// in the initial JS chunk. Hydrating all of them blocked main thread for
// seconds. Splitting them with `next/dynamic({ ssr: false })` keeps them
// out of the cold-path bundle; React mounts them post-paint as their JSX
// scrolls in or as the mobile breakpoint activates.
const MarketRelatedArticles = dynamic(() => import("@/app/market/[slug]/components/MarketRelatedArticles"), { ssr: false });
import type { BlogPostMeta } from "@/lib/blog-utils";
import { useDFlowMintsEvent } from "@/hooks/useDFlowMintsQuery";
import { useMarketPageQuery } from "@/hooks/useMarketPageQuery";
import PriceAlertModal from "@/app/components/PriceAlertModal";
const MarketTalks = dynamic(() => import("@/app/market/[slug]/components/MarketTalks"), { ssr: false });
import { useMatchingMarkets } from "@/hooks/useMatchingMarkets";
import { useUnifiedBalance } from "@/contexts/UnifiedBalanceContext";
import { type PriceStrike } from "./components/CryptoPriceLevelChart";
import CryptoPriceChart from "@/app/market/[slug]/components/CryptoPriceChart";

import { computeImpliedSpotFromStrikes, detectCryptoEvent, extractStrike, extractStrikes, filterNearestStrikes, findCurrentStrike, parseTickerTargetTime } from "@/lib/kalshi-crypto";
import { useCryptoPriceWs } from "@/hooks/useCryptoPriceWs";

import SportsMatchView from "./components/SportsMatchView";
import EventMarketsGrid from "./components/EventMarketsGrid";
import EventRewardsBadge from "./components/EventRewardsBadge";
import { EventSidebar } from "./components/EventSidebar";
const EventSmartMoney = dynamic(() => import("./components/EventSmartMoney"), { ssr: false });
const ResolutionDetails = dynamic(() => import("./components/ResolutionDetails"), { ssr: false });
const MobileBottomSheet = dynamic(() => import("./components/MobileBottomSheet"), { ssr: false });
const MobileMarketDetailOverlay = dynamic(() => import("./components/MobileMarketDetailOverlay"), { ssr: false });
const MobileEventStickyHeader = dynamic(() => import("./components/MobileEventStickyHeader"), { ssr: false });
const PredictTweetTracker = dynamic(() => import("./components/PredictTweetTracker"), { ssr: false });
const KalshiRelatedMarketsCard = dynamic(() => import("./components/KalshiRelatedMarketsCard"), { ssr: false });
import EventMyPositions from "./components/EventMyPositions";
import { getOutcomeAbbr, formatPriceCents } from "./components/types";
import type { Market } from "./components/types";
import { buildCanonicalPolymarketCryptoMarketSlug } from "./helpers/slug";
import { getPublicEventCategory } from "./helpers/categories";
import { FaqAnswerBody } from "./components/FaqAnswerBody";

export default function EventDetailPage({ source = 'polymarket', initialData, relatedArticles = [], faqs = [], initialCryptoCandles = null }: { source?: 'polymarket' | 'kalshi' | 'predict' | 'hyperliquid'; initialData?: EventData | null; relatedArticles?: BlogPostMeta[]; faqs?: Array<{ question: string; answer: string }>; initialCryptoCandles?: any[] | null } = {}) {
  const t = useT();
  const params = useParams();
  const router = useRouter();
  // Support both /event/[slug] and /event/kalshi/[eventTicker]/[[...slug]] routes
  const paramSlug = Array.isArray(params.slug) ? params.slug[0] : params.slug;
  const rawSlug = (params.eventTicker as string) || paramSlug || "";
  const slug = source === 'kalshi' ? rawSlug.toUpperCase() : rawSlug;

  // React Query + price prefetch only support the polymarket/kalshi/predict
  // hooks. For HL the entire `eventData` shape comes from SSR initialData
  // (no React Query refetch path exists yet), so we pass a narrowed source
  // and skip the prefetch — both are no-ops for HL with initialData supplied.
  const querySource = (source === 'hyperliquid' ? 'polymarket' : source) as 'polymarket' | 'kalshi' | 'predict';
  const { data: eventData = null, isLoading: loading, error: queryError } = useEventQuery(
    source === 'hyperliquid' ? '' : slug,
    querySource,
    initialData,
  );
  const error = queryError?.message || null;

  // Artículos de blog relacionados — fetch CLIENT-SIDE. MarketRelatedArticles es
  // dynamic(ssr:false) → nunca renderiza en el HTML SSR, así que traerlos en el
  // SSR (≈6 fetches al backend de blog ANTES de flushear HTML) solo inflaba el
  // TTFB del /event sin beneficio SEO. Ahora se piden tras montar. (perf 2026-06-03)
  const [clientArticles, setClientArticles] = useState<BlogPostMeta[]>(relatedArticles);
  useEffect(() => {
    const evSlug = eventData?.slug;
    if (!evSlug) return;
    const marketSlugs = (eventData?.markets || [])
      .map((m: any) => m.slug)
      .filter(Boolean)
      .slice(0, 5)
      .join(',');
    const qs = `slug=${encodeURIComponent(evSlug)}${marketSlugs ? `&markets=${encodeURIComponent(marketSlugs)}` : ''}`;
    let cancelled = false;
    fetch(`/api/blog/event-articles?${qs}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && Array.isArray(d?.articles)) setClientArticles(d.articles); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [eventData?.slug]);

  // Prefetch chart price history immediately (before chart component mounts)
  // This runs synchronously during render — no useEffect delay
  if (source !== 'hyperliquid' && eventData?.markets?.length) {
    prefetchPriceHistory(eventData.markets, querySource);
  }
  const tradingStore = useTradingModalStore();
  const balances = useUnifiedBalance();
  const [showKycModal, setShowKycModal] = useState(false);
  const proofKyc = useProofKYC();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileTradeOpen, setMobileTradeOpen] = useState(false);
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  // Support ?market=TICKER query param to pre-select a sub-market and
  // ?outcome=yes|no to highlight / pre-select the outcome.
  // IMPORTANT: use Next.js useSearchParams (reactive) instead of reading
  // window.location — useState's initializer is not re-run during hydration,
  // so `typeof window !== 'undefined'` returns null on SSR and the URL market
  // param was being lost.
  const searchParams = useSearchParams();
  // URL param can be either a market slug (preferred — shareable, SEO-friendly,
  // human-readable) or a numeric market id (legacy/fallback). We accept both
  // when reading; the matcher in `effectiveSelectedMarket` compares each
  // market's slug AND id, so state is source-agnostic.
  const urlMarketParam = searchParams?.get('market') || null;
  const urlOutcome = searchParams?.get('outcome') || null;
  const [selectedMarketId, setSelectedMarketId] = useState<string | null>(urlMarketParam);
  // Separate from selectedMarketId so that tapping the YES/NO pills on a
  // row only updates the sidebar TradingPanel selection WITHOUT expanding
  // (or collapsing) the inline OutcomeDetailPanel underneath. The row body
  // is still the thing that toggles the expanded panel.
  const [expandedMarketId, setExpandedMarketId] = useState<string | null>(urlMarketParam);
  // Tracks a market the user explicitly collapsed (clicked its row twice).
  // Consumed by EventMarketsGrid to skip the selectedMarketId fallback for
  // that specific row so the accordion stays closed until the user acts.
  const [userCollapsedMarketId, setUserCollapsedMarketId] = useState<string | null>(null);
  // Mobile-only: id of the market shown in the full-screen detail overlay.
  // Independent from selectedMarketId / expandedMarketId so the overlay can
  // open and close without disturbing the desktop accordion / sidebar state.
  const [mobileDetailMarketId, setMobileDetailMarketId] = useState<string | null>(null);
  // Selected outcome on the sidebar's TradingPanel, driven by clicks on the
  // grid's YES/NO buttons. Replaces the previous TradingModal flow on the
  // event page — sidebar is non-blocking, keeps chart/orderbook visible.
  const [selectedOutcomeIndex, setSelectedOutcomeIndex] = useState<0 | 1 | null>(
    urlOutcome === "yes" ? 0 : urlOutcome === "no" ? 1 : null
  );

  // Sidebar TradingPanel sell-mode driver. Two entry points feed it:
  //   1. URL params on initial mount (`?action=sell&outcome=...&shares=...`)
  //      — used by the portfolio HL "Sell" button which navigates here.
  //   2. The MyPositions Sell button on this very page — calls
  //      requestSellInPanel(position) and the panel switches without
  //      changing the URL or opening a modal.
  // The user's directive: "siempre el referente es el trading panel".
  const [panelSellSeed, setPanelSellSeed] = useState<{
    outcome: 'yes' | 'no';
    shares: number;
    avgPrice: number;
  } | null>(() => {
    if (searchParams?.get('action') !== 'sell') return null;
    const sharesParam = Number(searchParams?.get('shares') || 0);
    if (!Number.isFinite(sharesParam) || sharesParam <= 0) return null;
    const o = (searchParams?.get('outcome') || 'yes').toLowerCase();
    const outcome: 'yes' | 'no' = o === 'no' ? 'no' : 'yes';
    return { outcome, shares: sharesParam, avgPrice: 0 };
  });

  // MyPositions Sell button → drive the TradingPanel into SELL mode with
  // the position seeded. Also pin the panel to the right sub-market and
  // outcome side, and open the mobile bottom sheet (the panel lives in
  // there on phones). Defined before the early returns so all consumers
  // see the same callback identity.
  const requestSellInPanel = useCallback((position: any) => {
    const posOutcome = (position?.outcome || '').toLowerCase();
    const direction: 'yes' | 'no' = posOutcome === 'no' || posOutcome === 'down' ? 'no' : 'yes';
    setPanelSellSeed({
      outcome: direction,
      shares: Number(position?.shares ?? 0),
      avgPrice: Number(position?.avgPrice ?? 0),
    });
    // Match the outcome side on the panel so the SELL preset hits the
    // right token (otherwise a multi-outcome user would seed YES shares
    // while the panel still showed the previous NO selection).
    setSelectedOutcomeIndex(direction === 'yes' ? 0 : 1);
    // Mobile: panel lives in the bottom sheet, so open it.
    setMobileTradeOpen(true);
  }, []);

  // Hyperliquid: every HL "event" maps to exactly one binary question with
  // YES + NO sides — there is no multi-market grid to scroll through. Default
  // the OutcomeDetailPanel to expanded so chart / orderbook / trades show up
  // without forcing the user to click the row first (the previous behaviour
  // matched polymarket multi-market events but felt broken on HL singletons).
  useEffect(() => {
    if (source !== 'hyperliquid') return;
    if (expandedMarketId) return;
    const inner = eventData?.markets?.[0];
    if (!inner) return;
    const id = inner.slug || inner.id;
    if (id) setExpandedMarketId(id);
  }, [source, eventData?.markets, expandedMarketId]);

  // Remember the last market the user looked at (title only, 7d TTL consumed
  // by OnboardingModal). The onboarding fund step uses it to anchor the money
  // ask to THEIR market instead of generic copy — new signups almost always
  // register from an event page.
  useEffect(() => {
    const title = eventData?.title;
    if (!title) return;
    try {
      localStorage.setItem("ff:last-market", JSON.stringify({ title, ts: Date.now() }));
    } catch {}
  }, [eventData?.title]);

  // If the URL param arrives after mount (e.g. client-side navigation before
  // the URL is stable), sync the selected market once to match.
  useEffect(() => {
    if (urlMarketParam && urlMarketParam !== selectedMarketId) {
      setSelectedMarketId(urlMarketParam);
    }
    // Strip `?market=X` when it matches the event slug — there's no outcome
    // to pick on single-market events, so the param is pure noise.
    if (urlMarketParam && eventData?.slug && urlMarketParam === eventData.slug) {
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        url.searchParams.delete('market');
        window.history.replaceState({}, '', url.toString());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlMarketParam, eventData?.slug]);

  // Sync `?outcome=yes|no` URL changes into selectedOutcomeIndex. Without
  // this, MarketTalks post cards (which router.push with new outcome) flip
  // the URL but the sidebar state stays stuck on the previous side.
  useEffect(() => {
    const next = urlOutcome === "yes" ? 0 : urlOutcome === "no" ? 1 : null;
    setSelectedOutcomeIndex((prev) => (prev === next ? prev : next));
  }, [urlOutcome]);

  // Auto-select the winning outcome on resolved markets when the user
  // hasn't picked one explicitly. For Kalshi the URL never carries
  // ?outcome=, so opening a settled market would land on a null side
  // and the resolved-banner felt orphaned. Inferring from the prices
  // (1.0 / 0.0) puts the user straight on the winner without forcing
  // them to click.
  useEffect(() => {
    if (urlOutcome) return; // user / link already chose a side
    if (selectedOutcomeIndex !== null) return; // already settled
    if (!eventData?.markets) return;
    // Find a market in the event that's resolved with a clear winner.
    // Prefer the URL-selected market when present, fall back to the first
    // resolved market — gives the same auto-select on both single-market
    // links and event-overview clicks into a settled child.
    const target = (urlMarketParam &&
      eventData.markets.find(
        (m: any) => m.slug === urlMarketParam || m.id === urlMarketParam || m.conditionId === urlMarketParam,
      )) || eventData.markets.find((m: any) => m.closed);
    if (!target) return;
    const prices = (target as any).outcomePrices || [];
    if (prices.length !== 2) return;
    if (prices[0] >= 0.99) setSelectedOutcomeIndex(0);
    else if (prices[1] >= 0.99) setSelectedOutcomeIndex(1);
  }, [urlOutcome, urlMarketParam, eventData?.markets, selectedOutcomeIndex]);

  // Sync ?market=<slug|id> in the URL when the user picks an outcome. Prefer
  // slug over id because it's SEO-friendly and human-readable; we still look
  // it up by id below when slug isn't available.
  const syncUrlMarket = useCallback((marketIdentifier: string | null) => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (marketIdentifier) url.searchParams.set("market", marketIdentifier);
    else url.searchParams.delete("market");
    window.history.replaceState({}, "", url.toString());
  }, []);
  // Mirror market + outcome into the URL in a single history entry so reload
  // / share lands on the same trade context as the user's click.
  const syncUrlMarketOutcome = useCallback((marketIdentifier: string | null, outcomeIdx: 0 | 1 | null) => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (marketIdentifier) url.searchParams.set("market", marketIdentifier);
    else url.searchParams.delete("market");
    if (outcomeIdx === 0) url.searchParams.set("outcome", "yes");
    else if (outcomeIdx === 1) url.searchParams.set("outcome", "no");
    else url.searchParams.delete("outcome");
    window.history.replaceState({}, "", url.toString());
  }, []);
  // Click on grid YES/NO: select market + side.
  //  - Desktop (≥lg): sidebar's TradingPanel reflects the selection (no
  //    modal, non-blocking, chart/orderbook remain visible).
  //  - Mobile (<lg): open the bottom sheet — mobile has no persistent
  //    sidebar so the sheet is the equivalent trading surface.
  const handleSelectOutcome = useCallback((marketIdentifier: string, outcomeIdx: 0 | 1) => {
    setSelectedMarketId(marketIdentifier);
    setSelectedOutcomeIndex(outcomeIdx);
    syncUrlMarketOutcome(marketIdentifier, outcomeIdx);
    if (typeof window !== "undefined" && window.innerWidth < 1024) {
      setMobileTradeOpen(true);
    }
  }, [syncUrlMarketOutcome]);
  const [faqHovered, setFaqHovered] = useState(false);
  const [showAllMarkets, setShowAllMarkets] = useState(false);

  // DFlow mints: React Query, fires when tickers are available (from initialData = immediate)
  const dflowTickers = useMemo(() =>
    eventData?.markets?.map(m => m.id).filter(Boolean) || [],
    [eventData?.markets]
  );
  const { data: dflowMintsMap = {} } = useDFlowMintsEvent(dflowTickers, source === "kalshi");

  const getKalshiTicker = useCallback((market: any): string => {
    const raw = market?.ticker || market?.slug || market?.conditionId || market?.id || "";
    return String(raw).replace(/^ks-/i, "").toUpperCase();
  }, []);

  // Wrapper: intercept trading modal open for Kalshi — show KYC if not verified
  const openTradingModal = useCallback((params: {
    isOpen: boolean; marketSlug: string; outcome: "YES" | "NO";
    outcomePrice: number; marketQuestion: string; actualOutcome?: string; outcomes?: string[];
  } | null) => {
    if (!params) return;
    if (source === 'kalshi' && !proofKyc.loading && !proofKyc.verified && proofKyc.authenticated) {
      setShowKycModal(true);
      return;
    }
    const matchedKalshiMarket = eventData?.markets?.find((m: any) =>
      m.slug === params.marketSlug || m.id === params.marketSlug || m.ticker === params.marketSlug
    );
    const dflowTicker = matchedKalshiMarket ? getKalshiTicker(matchedKalshiMarket) : String(params.marketSlug || '').toUpperCase();
    const mints = dflowMintsMap[dflowTicker] || dflowMintsMap[params.marketSlug] || {};
    if (source === 'kalshi') {
      tradingStore.openKalshi({
        marketTicker: dflowTicker,
        dflowTicker,
        marketTitle: params.marketQuestion,
        outcome: params.outcome.toLowerCase() as 'yes' | 'no',
        currentPrice: params.outcomePrice,
        altPrice: 1 - params.outcomePrice,
        yesMint: (mints as any).yesMint,
        noMint: (mints as any).noMint,
        imageUrl: eventData?.image || undefined,
      });
    } else if (source === 'predict') {
      // Resolve real labels from the API outcomes pair. Pass them through
      // so the modal renders Over/Under, Up/Down, sports labels rather
      // than falling back to "Yes"/"No" inside.
      const sideIdx = params.outcome === "YES" ? 0 : 1;
      const altIdx = sideIdx === 0 ? 1 : 0;
      tradingStore.openPredict({
        marketId: params.marketSlug,
        marketSlug: params.marketSlug,
        outcome: params.outcome,
        outcomePrice: params.outcomePrice,
        marketQuestion: params.marketQuestion,
        marketImage: eventData?.image,
        altOutcomePrice: 1 - params.outcomePrice,
        outcomes: params.outcomes,
        actualOutcome: params.actualOutcome ?? params.outcomes?.[sideIdx],
        altActualOutcome: params.outcomes?.[altIdx],
      });
    } else if (source === 'hyperliquid') {
      // Pull asset_id off the matched HL market — its `id` carries the
      // YES-side asset_id (set in transformToEventData). Outcome index can
      // be derived from the assetId encoding (asset_id - 100_000_000 = 10*idx + side).
      const matchedHl = eventData?.markets?.find((m: any) => m.slug === params.marketSlug);
      const yesAssetId = Number((matchedHl as any)?.id || 0);
      const sideOffset = params.outcome === 'NO' ? 1 : 0;
      const assetId = yesAssetId ? yesAssetId + sideOffset : 0;
      const outcomeIndex = assetId >= 100_000_000 ? Math.floor((assetId - 100_000_000) / 10) : 0;
      tradingStore.openHyperliquid({
        marketSlug: params.marketSlug,
        outcomeIndex,
        assetId,
        outcome: params.outcome,
        outcomePrice: params.outcomePrice,
        marketQuestion: params.marketQuestion,
        marketImage: eventData?.image,
        altOutcomePrice: 1 - params.outcomePrice,
        outcomes: params.outcomes,
      });
    } else {
      const matchedMarket = eventData?.markets?.find(m => m.slug === params.marketSlug || m.id === params.marketSlug);
      tradingStore.openPolymarket({
        marketSlug: params.marketSlug,
        outcome: params.outcome,
        outcomePrice: params.outcomePrice,
        marketQuestion: params.marketQuestion,
        marketImage: eventData?.image,
        actualOutcome: params.actualOutcome,
        altOutcomePrice: 1 - params.outcomePrice,
        outcomes: params.outcomes,
        clobTokenIds: matchedMarket?.clobTokenIds || [],
        conditionId: matchedMarket?.conditionId,
      });
    }
  }, [source, proofKyc.loading, proofKyc.verified, proofKyc.authenticated, dflowMintsMap, eventData?.image, eventData?.markets, tradingStore, getKalshiTicker]);

  const { heroRef, scrollY } = useParallaxScroll();

  // Widget config: fires immediately in parallel with event data (React Query)
  const { data: widgetConfig = null } = useEventWidgetConfig(slug);

  useDwellTracking(slug, eventData);

  // tokenId → outcome display name, built from all event markets so we can
  // resolve PM comment positions back to a readable outcome ("90 Oklahoma
  // City Thunder"). Uses groupItemTitle first (multi-outcome events) and
  // falls back to the outcome label on the market side.
  const pmTokenIdToOutcome = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    const ms = (eventData?.markets || []) as any[];
    for (const m of ms) {
      const ids: string[] = Array.isArray(m?.clobTokenIds) ? m.clobTokenIds : [];
      const outcomes: string[] = Array.isArray(m?.outcomes) ? m.outcomes : [];
      const label = m?.groupItemTitle || m?.question || '';
      ids.forEach((id, i) => {
        if (!id) return;
        const outcomeLabel = outcomes[i] || '';
        // Multi-outcome events: label is the candidate/team name; the YES
        // side always represents "that outcome wins". Use label alone.
        // Binary events: label is the question → fall back to YES/NO.
        map[id] = label || outcomeLabel || `Outcome ${i + 1}`;
      });
    }
    return map;
  }, [eventData?.markets]);

  // Stable reference for active (non-closed) markets, sorted by date ascending
  const activeMarkets = useMemo(() => {
    if (!eventData?.markets?.length) return [];
    const active = eventData.markets.filter((m: any) => {
      // Kalshi markets expose `status` instead of active/closed
      if (m.status !== undefined && m.active === undefined) {
        return m.status === 'open' || m.status === 'active';
      }
      return m.active && !m.closed;
    });
    // Sort by endDate — preserve backend order when dates are equal
    // (Kalshi returns markets ordered by strike/ticker which is the correct display order)
    const parseDate = (m: any): number => {
      if (m.endDate) return new Date(m.endDate).getTime();
      const title = m.groupItemTitle || '';
      const match = title.match(/^(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2})/i);
      if (match) {
        const year = new Date().getFullYear();
        const parsed = new Date(`${match[1]} ${match[2]}, ${year}`);
        if (parsed.getTime() < Date.now() - 90 * 86400000) parsed.setFullYear(year + 1);
        if (!isNaN(parsed.getTime())) return parsed.getTime();
      }
      return Infinity;
    };
    // Check if all dates are the same — if so, sort by YES probability desc
    // (mutually-exclusive markets like election nominees share one end date;
    // kalshi.com surfaces leaders at the top). When dates differ, sort asc by
    // deadline. Kalshi markets expose `yesPrice` directly; Polymarket uses
    // `outcomePrices[0]`.
    const dates = active.map(parseDate);
    const allSameDate = dates.every(d => d === dates[0]);
    if (allSameDate) {
      // Price-ladder events ("What price will BTC hit June 1-7?" — every
      // outcome is an ↑/↓ numeric threshold). Polymarket displays these
      // strictly by price level DESCENDING (its `groupItemThreshold` order:
      // ↑86k…↑74k, then ↓66k…↓58k). Sorting by YES probability instead
      // interleaves the ↑/↓ rungs and looks jumbled (reported 2026-06-02 on
      // exactly this BTC event). `groupItemThreshold` isn't preserved by our
      // backend, so we derive the level from the title and mirror PM's order.
      // Gated on the ↑/↓ + number title shape so it never touches nominee /
      // team / date / count events (which keep the YES-desc "leaders on top").
      const levelOf = (m: any): number => {
        const t = String(m?.groupItemTitle || '').trim();
        if (!/^[↑↓]/.test(t)) return NaN;
        const n = Number(t.replace(/[^\d.]/g, ''));
        return Number.isFinite(n) ? n : NaN;
      };
      const levels = active.map(levelOf);
      if (levels.length >= 2 && levels.every((n) => Number.isFinite(n))) {
        return [...active].sort((a, b) => levelOf(b) - levelOf(a));
      }
      const yesOf = (m: any): number => {
        const p = typeof m?.yesPrice === 'number'
          ? m.yesPrice
          : Number(m?.outcomePrices?.[0]);
        return Number.isFinite(p) ? p : 0;
      };
      return [...active].sort((a, b) => yesOf(b) - yesOf(a));
    }
    return [...active].sort((a, b) => parseDate(a) - parseDate(b));
  }, [eventData?.markets]);

  // For closed events, use all markets for the chart (so users can see historical prices)
  const chartMarkets = useMemo(() => {
    if (!eventData?.markets?.length) return [];
    // If event is closed, show all markets; otherwise just active ones
    return eventData.closed ? eventData.markets : activeMarkets;
  }, [eventData?.markets, eventData?.closed, activeMarkets]);

  // Deadline event detection: every outcome title is a "Month Day" / "by X"
  // date (e.g. "April 22", "May 31"). These are cumulative deadlines, not
  // competing outcomes, so a single overlaid line chart doesn't make sense
  // — PM hides the chart entirely on these and we replace it with a simple
  // horizontal bar ladder of current probabilities.
  const isDeadlineEvent = useMemo(() => {
    if (!chartMarkets || chartMarkets.length < 2) return false;
    const datePattern = /^(by\s+)?(january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec)\s+\d{1,2}(st|nd|rd|th)?(,?\s*\d{4})?$/i;
    return chartMarkets.every((m) => {
      const label = (m.groupItemTitle || m.question || '').trim();
      return datePattern.test(label);
    });
  }, [chartMarkets]);

  // ─── Polymarket price-level crypto detection ────────────────────────────
  // Events like "What price will Bitcoin hit on April 20?" carry N markets
  // with titles "↑ 82,000" / "↓ 74,000" (or "Will Bitcoin reach $X"). The
  // standard multi-outcome line chart is all noise here — show the real
  // live price with horizontal strike lines instead.
  const pmPriceLevelInfo = useMemo(() => {
    if (source !== 'polymarket') return { isPriceLevel: false as const };
    const ms = chartMarkets || [];
    if (ms.length < 3) return { isPriceLevel: false as const };
    const title = (eventData?.title || '').toLowerCase();
    const cryptoMap: Record<string, { symbol: string; polymarketSymbol: string }> = {
      bitcoin: { symbol: 'BTC', polymarketSymbol: 'btcusdt' },
      btc: { symbol: 'BTC', polymarketSymbol: 'btcusdt' },
      ethereum: { symbol: 'ETH', polymarketSymbol: 'ethusdt' },
      ether: { symbol: 'ETH', polymarketSymbol: 'ethusdt' },
      eth: { symbol: 'ETH', polymarketSymbol: 'ethusdt' },
      solana: { symbol: 'SOL', polymarketSymbol: 'solusdt' },
      sol: { symbol: 'SOL', polymarketSymbol: 'solusdt' },
      xrp: { symbol: 'XRP', polymarketSymbol: 'xrpusdt' },
      doge: { symbol: 'DOGE', polymarketSymbol: 'dogeusdt' },
      dogecoin: { symbol: 'DOGE', polymarketSymbol: 'dogeusdt' },
      hype: { symbol: 'HYPE', polymarketSymbol: 'hypeusdt' },
      hyperliquid: { symbol: 'HYPE', polymarketSymbol: 'hypeusdt' },
      bnb: { symbol: 'BNB', polymarketSymbol: 'bnbusdt' },
    };
    // Equity / forex / metals / commodities map. Pyth tickers are
    // lowercase, no slash — see backend/src/services/crypto-price-ws.ts
    // EQUITY_SYMBOLS for the full set the WS actually subscribes to.
    // Keys are title-substring hints we'll match against the event title.
    const equityMap: Record<string, { symbol: string; polymarketSymbol: string }> = {
      tesla: { symbol: 'TSLA', polymarketSymbol: 'tsla' },
      tsla: { symbol: 'TSLA', polymarketSymbol: 'tsla' },
      apple: { symbol: 'AAPL', polymarketSymbol: 'aapl' },
      aapl: { symbol: 'AAPL', polymarketSymbol: 'aapl' },
      microsoft: { symbol: 'MSFT', polymarketSymbol: 'msft' },
      msft: { symbol: 'MSFT', polymarketSymbol: 'msft' },
      alphabet: { symbol: 'GOOG', polymarketSymbol: 'goog' },
      google: { symbol: 'GOOG', polymarketSymbol: 'goog' },
      amazon: { symbol: 'AMZN', polymarketSymbol: 'amzn' },
      amzn: { symbol: 'AMZN', polymarketSymbol: 'amzn' },
      meta: { symbol: 'META', polymarketSymbol: 'meta' },
      nvidia: { symbol: 'NVDA', polymarketSymbol: 'nvda' },
      nvda: { symbol: 'NVDA', polymarketSymbol: 'nvda' },
      netflix: { symbol: 'NFLX', polymarketSymbol: 'nflx' },
      palantir: { symbol: 'PLTR', polymarketSymbol: 'pltr' },
      pltr: { symbol: 'PLTR', polymarketSymbol: 'pltr' },
      opendoor: { symbol: 'OPEN', polymarketSymbol: 'open' },
      'rocket lab': { symbol: 'RKLB', polymarketSymbol: 'rklb' },
      airbnb: { symbol: 'ABNB', polymarketSymbol: 'abnb' },
      coinbase: { symbol: 'COIN', polymarketSymbol: 'coin' },
      robinhood: { symbol: 'HOOD', polymarketSymbol: 'hood' },
      qqq: { symbol: 'QQQ', polymarketSymbol: 'qqq' },
      'sp 500': { symbol: 'SPY', polymarketSymbol: 'spy' },
      's&p 500': { symbol: 'SPY', polymarketSymbol: 'spy' },
      'eur/usd': { symbol: 'EURUSD', polymarketSymbol: 'eurusd' },
      'gbp/usd': { symbol: 'GBPUSD', polymarketSymbol: 'gbpusd' },
      'usd/jpy': { symbol: 'USDJPY', polymarketSymbol: 'usdjpy' },
      gold: { symbol: 'XAUUSD', polymarketSymbol: 'xauusd' },
      silver: { symbol: 'XAGUSD', polymarketSymbol: 'xagusd' },
      'crude oil': { symbol: 'WTI', polymarketSymbol: 'wti' },
      'natural gas': { symbol: 'NATGAS', polymarketSymbol: 'natgas' },
    };
    let crypto: { symbol: string; polymarketSymbol: string } | null = null;
    let assetClass: 'crypto' | 'equity' = 'crypto';
    for (const key of Object.keys(cryptoMap)) {
      if (title.includes(key)) { crypto = cryptoMap[key]; break; }
    }
    if (!crypto) {
      for (const key of Object.keys(equityMap)) {
        if (title.includes(key)) { crypto = equityMap[key]; assetClass = 'equity'; break; }
      }
    }
    if (!crypto) return { isPriceLevel: false as const };

    // Parse strikes from titles:
    //   "↑ 82,000" | "↓ 74,000"           (arrow)
    //   "reach $82,000"                    (reach)
    //   "dip to $74,000"                   (dip)
    //   "68,000" / "$70K" / "1.2M"         (plain — Polymarket's recurring
    //                                       BTC/ETH/SOL "above ___ on May Y"
    //                                       template ships sub-markets with
    //                                       a bare numeric `groupItemTitle`.
    //                                       Direction comes from the event
    //                                       title keyword: "above"/"reach"
    //                                       → side=reach; "below"/"under"
    //                                       /"dip" → side=dip. Without that
    //                                       cue we don't guess.)
    const strikes: PriceStrike[] = [];
    const arrow = /([↑↓])\s*\$?([\d,]+(?:\.\d+)?)\s*[KkMm]?/;
    const reachRe = /reach\s+\$?([\d,]+(?:\.\d+)?)\s*([KkMm])?/i;
    const dipRe = /dip\s+to\s+\$?([\d,]+(?:\.\d+)?)\s*([KkMm])?/i;
    const plainRe = /^\s*\$?([\d,]+(?:\.\d+)?)\s*([KkMm])?\s*$/;
    const plainSide: 'reach' | 'dip' | null = (() => {
      if (/\b(above|reach|exceed|hit|over)\b/.test(title)) return 'reach';
      if (/\b(below|under|dip)\b/.test(title)) return 'dip';
      return null;
    })();
    const shortUsd = (n: number): string => {
      if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
      if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
      return `$${n.toFixed(0)}`;
    };
    const parseNum = (raw: string, suffix?: string): number => {
      let n = Number(raw.replace(/,/g, ''));
      if (!Number.isFinite(n)) return NaN;
      if (suffix?.toLowerCase() === 'k') n *= 1_000;
      if (suffix?.toLowerCase() === 'm') n *= 1_000_000;
      return n;
    };
    for (const m of ms) {
      const label = ((m as any).groupItemTitle || (m as any).question || '').trim();
      let matched = false;
      const ma = label.match(arrow);
      if (ma) {
        const n = parseNum(ma[2]);
        if (Number.isFinite(n)) {
          strikes.push({ price: n, side: ma[1] === '↑' ? 'reach' : 'dip', label: (m as any).groupItemTitle || `${ma[1]} ${shortUsd(n)}` });
          matched = true;
        }
      }
      if (!matched) {
        const mr = reachRe.exec(label);
        if (mr) {
          const n = parseNum(mr[1], mr[2]);
          if (Number.isFinite(n)) {
            strikes.push({ price: n, side: 'reach', label: `↑ ${shortUsd(n)}` });
            matched = true;
          }
        }
      }
      if (!matched) {
        const md = dipRe.exec(label);
        if (md) {
          const n = parseNum(md[1], md[2]);
          if (Number.isFinite(n)) {
            strikes.push({ price: n, side: 'dip', label: `↓ ${shortUsd(n)}` });
            matched = true;
          }
        }
      }
      if (!matched && plainSide) {
        const mp = plainRe.exec(label);
        if (mp) {
          const n = parseNum(mp[1], mp[2]);
          if (Number.isFinite(n)) {
            strikes.push({ price: n, side: plainSide, label: shortUsd(n) });
          }
        }
      }
    }
    if (strikes.length < 3) return { isPriceLevel: false as const };
    return { isPriceLevel: true as const, crypto, strikes, assetClass };
  }, [source, chartMarkets, eventData?.title]);

  // Hyperliquid Recurring crypto detection. The HL page parses the
  // `class:priceBinary|...` (one strike) or `class:priceBucket|...`
  // (N strikes from priceThresholds) description and stamps
  // `eventData.hlPriceBinary` / `hlPriceBucket`. When either is present we
  // render the same live-underlying-with-strike-line chart used for
  // Polymarket / Kalshi crypto events — one dashed line per threshold for
  // bucket events, one for binaries — instead of the noisy HIP-4 candle
  // chart on the outcome's own midpoint.
  const hlPriceLevelInfo = useMemo(() => {
    if (source !== 'hyperliquid') return { isPriceLevel: false as const };
    const pb = (eventData as any)?.hlPriceBinary as
      | { underlying: string; targetPrice: number; expiryISO: string; period: string | null }
      | undefined;
    const pBucket = (eventData as any)?.hlPriceBucket as
      | { underlying: string; thresholds: number[]; expiryISO: string; period: string | null }
      | undefined;
    if (!pb && !pBucket) return { isPriceLevel: false as const };
    const underlying = (pb?.underlying || pBucket?.underlying || '').toUpperCase();
    const expiryISO = pb?.expiryISO || pBucket?.expiryISO || '';
    const period = pb?.period ?? pBucket?.period ?? null;
    const symbolMap: Record<string, string> = {
      BTC: 'btcusdt', ETH: 'ethusdt', SOL: 'solusdt',
      XRP: 'xrpusdt', DOGE: 'dogeusdt', BNB: 'bnbusdt',
      HYPE: 'hypeusdt',
    };
    const polymarketSymbol = symbolMap[underlying];
    if (!polymarketSymbol) return { isPriceLevel: false as const };
    const shortUsd = (n: number): string => {
      if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
      if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
      return `$${n.toFixed(0)}`;
    };
    // Build the strike list: one for binary, N for bucket (sorted asc).
    const strikes = pBucket
      ? [...pBucket.thresholds]
          .sort((a, b) => a - b)
          .map((px) => ({ price: px, side: 'reach' as const, label: shortUsd(px) }))
      : pb
        ? [{ price: pb.targetPrice, side: 'reach' as const, label: `↑ ${shortUsd(pb.targetPrice)}` }]
        : [];
    if (strikes.length === 0) return { isPriceLevel: false as const };
    // Map HL period → CryptoPriceChart's supported intervals. The chart
    // uses the interval to size its rolling Liveline window. HL Recurring
    // questions ship 5m/15m/1h/4h/1d/1w; the chart only knows up to 4h,
    // so longer periods round down to 4h (still gives a meaningful
    // window). null→default 15m inside the chart.
    const intervalFromPeriod = (p: string | null): "5m" | "15m" | "1h" | "4h" | null => {
      if (!p) return null;
      if (p === '5m' || p === '15m' || p === '1h' || p === '4h') return p;
      // 1d / 1w / 1m → cap at 4h so the chart window is the resolution
      // bucket without exceeding what CryptoPriceChart supports.
      return '4h';
    };
    const interval = intervalFromPeriod(period);
    // Derive event start = expiry - period. Without it the chart can't
    // size its initial visible window or render the open-price marker.
    const periodMs: Record<string, number> = {
      '5m': 5 * 60_000, '15m': 15 * 60_000, '1h': 60 * 60_000,
      '4h': 4 * 60 * 60_000, '1d': 24 * 60 * 60_000, '1w': 7 * 24 * 60 * 60_000,
    };
    const eventStartISO = period && periodMs[period] && expiryISO
      ? new Date(new Date(expiryISO).getTime() - periodMs[period]).toISOString()
      : null;
    return {
      isPriceLevel: true as const,
      crypto: { symbol: underlying, polymarketSymbol },
      strikes,
      expiryISO,
      eventStartISO,
      interval,
    };
  }, [source, eventData]);

  // ─── Crypto price-level event detection ─────────────────────────────────
  const cryptoInfo = useMemo(() => detectCryptoEvent(eventData?.seriesTicker), [eventData?.seriesTicker]);

  // Current crypto price now arrives via the shared crypto:price WebSocket
  // stream (Polymarket Data Streams → backend → Socket.IO). The previous
  // 2s S3 poll hit `/api/kalshi-crypto` for every viewer of every Kalshi
  // crypto event — at scale that was thousands of requests/min for a
  // public market price. WS gives near-instant updates and zero per-viewer
  // cost. The 6h-open value still comes from S3 but only refreshes every
  // 60s since it's a slow-moving anchor for the price-change chip.
  const wsCryptoPrices = useCryptoPriceWs(cryptoInfo.isCrypto);
  const wsPrice = cryptoInfo.isCrypto ? wsCryptoPrices[cryptoInfo.symbol]?.price : undefined;
  const currentCryptoPrice = wsPrice ?? null;
  const [s3Open6H, setS3Open6H] = useState<number | null>(null);
  useEffect(() => {
    if (!cryptoInfo.isCrypto) return;

    const fetchOpen6H = () => {
      fetch(`/api/kalshi-crypto?asset=${cryptoInfo.s3Prefix}&type=current`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          const open6h = data?.candlesticks?.['6H']?.open;
          if (open6h) setS3Open6H(open6h);
        })
        .catch(() => {});
    };

    fetchOpen6H();
    // 6h-open is a slow-moving anchor — 60s is plenty.
    const iv = setInterval(fetchOpen6H, 60_000);
    return () => clearInterval(iv);
  }, [cryptoInfo]);

  // Extract strikes from all active markets. Previously gated on
  // cryptoInfo.isCrypto so only BTC/ETH/SOL events got the strike-
  // filtering treatment. Generalised so any Kalshi event whose markets
  // expose extractable strikes (KXWTI oil, KXNDX index, …) can fall
  // back to the implied-spot path below.
  const allStrikes = useMemo(() => {
    if (source !== 'kalshi') return [];
    return extractStrikes(activeMarkets);
  }, [source, activeMarkets]);

  // Implied spot from market prices — used when no external WS price
  // feed exists for the underlying. The strike whose YES sits closest
  // to 50¢ is, by construction, the book's view of "current". Strikes
  // pegged to 0¢/100¢ are ignored so a dead-zone tail doesn't anchor
  // the centre on a resolved-ish strike. Reads yes price directly from
  // the market record (Kalshi yesPrice or Polymarket outcomePrices[0])
  // so we don't depend on the getLivePrice callback declared later.
  const impliedSpotPrice = useMemo(() => {
    if (source !== 'kalshi' || allStrikes.length < 3) return null;
    return computeImpliedSpotFromStrikes(allStrikes, (idx) => {
      const m = activeMarkets[idx] as any;
      if (!m) return null;
      if (typeof m.yesPrice === 'number') return m.yesPrice;
      if (Array.isArray(m.outcomePrices) && m.outcomePrices[0] != null) {
        const v = parseFloat(String(m.outcomePrices[0]));
        return Number.isNaN(v) ? null : v;
      }
      return null;
    });
  }, [source, allStrikes, activeMarkets]);

  // External spot wins (crypto has a live WS feed); else fall back to
  // the implied spot pulled out of the order book.
  const effectiveSpotPrice = currentCryptoPrice ?? impliedSpotPrice;

  // Current strike index (market index that contains the current price)
  const currentStrikeMarketIndex = useMemo(() => {
    if (!cryptoInfo.isCrypto || currentCryptoPrice == null) return null;
    return findCurrentStrike(allStrikes, currentCryptoPrice);
  }, [cryptoInfo.isCrypto, currentCryptoPrice, allStrikes]);

  // Filter to nearest ~5 strikes for display. Crypto events can have
  // 100+ markets, commodity / oil events ~30 — both render unusable
  // walls of 99% strikes without this trim. Driven by effectiveSpot
  // so non-crypto strike events also benefit.
  const [showAllCryptoMarkets, setShowAllCryptoMarkets] = useState(false);
  const shouldFilterStrikes = source === 'kalshi' && allStrikes.length >= 3 && effectiveSpotPrice != null && !showAllCryptoMarkets;
  const displayedActiveMarkets = useMemo(() => {
    if (!shouldFilterStrikes || effectiveSpotPrice == null) return activeMarkets;
    const nearestIndices = new Set(filterNearestStrikes(allStrikes, effectiveSpotPrice, 5));
    if (nearestIndices.size === 0) return activeMarkets;
    return activeMarkets.filter((_, i) => nearestIndices.has(i));
  }, [shouldFilterStrikes, effectiveSpotPrice, allStrikes, activeMarkets]);

  // Switch to next active crypto event (used on page load if closed + live countdown expiry)
  const hasRedirected = useRef(false);
  const switchToNextCryptoEvent = useCallback(() => {
    if (!cryptoInfo.isCrypto || hasRedirected.current) return;
    hasRedirected.current = true;
    fetch(`/api/kalshi-active?series=${cryptoInfo.seriesTicker}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.activeEvent?.ticker && data.activeEvent.ticker.toUpperCase() !== slug.toUpperCase()) {
          const ticker = data.activeEvent.ticker;
          const titleSlug = (data.activeEvent.title || ticker)
            .toLowerCase().replace(/['']/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 80);
          router.replace(`/kalshi/event/${ticker}/${titleSlug}`);
        } else {
          // Next event not available yet — retry in 5s
          hasRedirected.current = false;
          setTimeout(switchToNextCryptoEvent, 5000);
        }
      })
      .catch(() => { hasRedirected.current = false; });
  }, [cryptoInfo, slug, router]);

  // Auto-redirect on page load if event is already closed
  useEffect(() => {
    if (!cryptoInfo.isCrypto || !eventData || hasRedirected.current) return;
    const allClosed = eventData.markets?.every(m => m.closed || !m.active);
    if (allClosed) switchToNextCryptoEvent();
  }, [cryptoInfo, eventData, switchToNextCryptoEvent]);

  // Target time for countdown (parsed from event ticker, NOT closeTime which is 7 days out)
  const cryptoCloseDate = useMemo(() => {
    if (!cryptoInfo.isCrypto) return null;
    return parseTickerTargetTime(slug) || null;
  }, [cryptoInfo.isCrypto, slug]);

  // Auto-select market: crypto → current strike, others → first/highest volume.
  // For finished events activeMarkets is empty; fall back to the raw markets
  // list so the sidebar / resolution details / FAQs still render.
  // We also mirror the pick into ?market=<id> via replaceState so the URL
  // reflects what's on screen — shareable links land on the same outcome
  // instead of silently re-picking a different one on the recipient's side.
  const lastAutoSelectedStrike = useRef<number | null>(null);
  useEffect(() => {
    // When the auto-selected market matches the event itself (single-market
    // events like binary crypto up/down, single-outcome deadlines), skip
    // writing `?market=X` — the URL already points at the event.
    const writeUrlIfDifferent = (id: string) => {
      if (id && eventData?.slug && id === eventData.slug) return;
      syncUrlMarket(id);
    };

    // Crypto: select current strike (update when strike changes)
    if (cryptoInfo.isCrypto && currentStrikeMarketIndex != null && activeMarkets.length > 0) {
      if (lastAutoSelectedStrike.current !== currentStrikeMarketIndex) {
        lastAutoSelectedStrike.current = currentStrikeMarketIndex;
        const target = activeMarkets[currentStrikeMarketIndex] || activeMarkets[0];
        const id = target.slug || target.id;
        setSelectedMarketId(id);
        writeUrlIfDifferent(id);
      }
      return;
    }

    // Non-crypto: only auto-select on single-market events (binary
    // YES/NO, single-deadline futures, etc.) where there's no real
    // "pick" to make. Multi-outcome events must land with NOTHING
    // selected — neither in the URL nor in the sticky CTA / sidebar
    // TradingPanel. The user picks the side they want by tapping a row
    // or the YES/NO buttons; pre-selecting the highest-volume sub-market
    // pushes a phantom side onto them.
    if (!selectedMarketId && !eventData?.closed) {
      const pool = activeMarkets.length > 0 ? activeMarkets : (eventData?.markets as any[]) || [];
      if (pool.length !== 1) return;
      const target = pool[0];
      const identifier = target?.slug || target?.id;
      if (!identifier) return;
      setSelectedMarketId(identifier);
      writeUrlIfDifferent(identifier);
    }
  }, [activeMarkets, selectedMarketId, source, cryptoInfo.isCrypto, currentStrikeMarketIndex, eventData?.markets, eventData?.closed, eventData?.slug, syncUrlMarket]);

  // Target market: active market with highest volume (or highest volume overall)
  const targetMarket = useMemo(() => {
    if (!eventData?.markets?.length) return null;
    return activeMarkets.length > 0
      ? activeMarkets.reduce((best, m) => (m.volume || 0) > (best.volume || 0) ? m : best, activeMarkets[0])
      : eventData.markets.reduce((best, m) => (m.volume || 0) > (best.volume || 0) ? m : best, eventData.markets[0]);
  }, [eventData?.markets, activeMarkets]);

  // Selected market for trading sidebar (defaults to highest probability).
  // For finished events (all markets settled) fall back to the raw markets
  // list so the sidebar / resolution details / FAQs still render post-match.
  const selectedMarket = useMemo(() => {
    const pool = activeMarkets.length
      ? activeMarkets
      : ((eventData?.markets || []) as typeof activeMarkets);
    if (!pool.length) return null;
    if (selectedMarketId) {
      const found = pool.find(m =>
        m.id === selectedMarketId
        || m.slug === selectedMarketId
        || m.id === `ks-${selectedMarketId.toUpperCase()}`
        || m.slug === selectedMarketId.toLowerCase()
      );
      if (found) return found;
    }
    const exactEventMarket = pool.find((market) =>
      market.slug === slug
      || market.id === slug
      || market.slug === eventData?.slug
      || market.id === eventData?.slug
    );
    if (exactEventMarket) return exactEventMarket;
    // For closed events, default to the winning market (resolved with price >= 0.95)
    if (eventData?.closed) {
      const winner = pool.find(m => m.closed && Number(m.outcomePrices?.[0] ?? 0) >= 0.95);
      return winner || null;
    }
    return pool[0];
  }, [activeMarkets, selectedMarketId, eventData?.markets, eventData?.closed, eventData?.slug, slug]);

  // ── DexTools signup interstitial context ──────────────────────────────
  // When a visitor lands from the DexTools widget, feed the global signup
  // modal the REAL selected-market context (title, image, side, price, outcome
  // name + crest) from our data — not a DOM scrape. DextoolsWelcomeBanner reads
  // this and renders the mirror modal. Null when it's not a DexTools landing.
  const setDextoolsSignupCtx = useDextoolsSignupStore((s) => s.setCtx);
  useEffect(() => {
    try {
      const utm = detectAndStoreDextoolsUtm();
      // Only the `predictions` campaign feeds this market modal. `copytrading`
      // arrivals (trader profiles) are handled by UserProfileClient → traderCtx;
      // gating here keeps the two flows from ever crossing wires.
      if (!utm || utm.utmCampaign !== 'predictions' || !selectedMarket) {
        setDextoolsSignupCtx(null);
        return;
      }
      const m = selectedMarket as unknown as {
        yesPrice?: number;
        outcomePrices?: Array<string | number>;
        groupItemTitle?: string;
        question?: string;
        image?: string;
        volume?: number | string;
      };
      // Per-outcome crest: a country flag (World-Cup-style, derived from the
      // outcome title) when available, else the market's own image. Matches how
      // EventMarketsGrid resolves each row's image.
      const crestFor = (mm: { groupItemTitle?: string; image?: string }) =>
        getCountryFlagUrl(mm.groupItemTitle) || mm.image || null;
      // Only show a specific outcome (name + crest + side + price) when the
      // deep-link actually targeted one (?outcome=yes|no). A bare event-level
      // arrival shouldn't fabricate "France · YES" — it shows the event only.
      const hasOutcome = urlOutcome === "yes" || urlOutcome === "no";
      const side: "yes" | "no" | null = hasOutcome ? (urlOutcome as "yes" | "no") : null;
      const yesP = typeof m.yesPrice === "number" ? m.yesPrice : Number(m.outcomePrices?.[0]);
      const frac = side === "no" ? 1 - yesP : yesP;
      // Event-level (no specific outcome) → a preview of the outcomes' crests
      // for a "pick your side" stack.
      const allMarkets = (activeMarkets.length ? activeMarkets : (eventData?.markets || [])) as unknown as Array<{ image?: string; groupItemTitle?: string }>;
      const outcomeImages = hasOutcome
        ? undefined
        : allMarkets.map(crestFor).filter((s): s is string => !!s).slice(0, 6);
      setDextoolsSignupCtx({
        marketTitle: eventData?.title || "",
        marketImage: eventData?.image || null,
        outcomeName: hasOutcome ? (m.groupItemTitle || null) : null,
        outcomeImage: hasOutcome ? crestFor(m) : null,
        side,
        priceCents: hasOutcome && Number.isFinite(frac) && frac > 0 && frac < 1 ? Math.round(frac * 100) : null,
        outcomeImages,
        outcomeCount: hasOutcome ? undefined : allMarkets.length,
        // Specific outcome → that market's volume; event-level → whole-event volume.
        volumeUsd: (() => {
          const n = Number(hasOutcome ? m.volume : eventData?.volume);
          return Number.isFinite(n) && n > 0 ? n : null;
        })(),
      });
    } catch {
      setDextoolsSignupCtx(null);
    }
  }, [selectedMarket, activeMarkets, eventData?.title, eventData?.image, eventData?.volume, urlOutcome, setDextoolsSignupCtx]);
  // Clear when leaving the event page so the modal doesn't linger elsewhere.
  useEffect(() => () => setDextoolsSignupCtx(null), [setDextoolsSignupCtx]);

  // Fetch full market details for selected market (createdAt, resolvedBy, description, etc.)
  // Skip for Predict — event description already comes from eventData, no Polymarket/UMA data needed
  const isKalshi = source === 'kalshi';
  const isPredict = source === 'predict';
  const selectedMarketLooksLikePolymarketCrypto = source === 'polymarket'
    && !!selectedMarket
    && (
      selectedMarket.slug?.includes('updown')
      || (selectedMarket.question || '').toLowerCase().includes('up or down')
    );
  const canonicalPolymarketCryptoMarketSlug = useMemo(() => {
    if (!selectedMarketLooksLikePolymarketCrypto || !selectedMarket) return null;
    return buildCanonicalPolymarketCryptoMarketSlug(
      selectedMarket.question || eventData?.title || null,
      selectedMarket.endDate || eventData?.endDate || null,
    );
  }, [selectedMarketLooksLikePolymarketCrypto, selectedMarket, eventData?.title, eventData?.endDate]);
  // For crypto up/down markets with epoch slugs (btc-updown-5m-1776180300),
  // use the actual slug — the canonical slug builder generates wrong slugs for these
  const selectedMarketDetailSlug = isPredict
    ? ''
    : isKalshi
      ? (selectedMarket?.slug || '')
      : (selectedMarket?.slug?.match(/-\d{10}$/) ? selectedMarket.slug : (canonicalPolymarketCryptoMarketSlug || selectedMarket?.slug || ''));
  const { data: selectedMarketDetails } = useMarketPageQuery(
    selectedMarketDetailSlug,
    isKalshi,
    undefined,
    // Hyperliquid: never hit /api/polymarket/market/<slug>; the URL 404s
    // for HL slugs and the failed query keeps the page in a re-render
    // storm (#300). The hook returns initialData (or null) without
    // touching the network.
    source === 'hyperliquid',
  );
  const fallbackPolymarketCryptoMarket = useMemo(() => {
    if (!selectedMarketLooksLikePolymarketCrypto || !selectedMarket) return null;

    const resolvedEndDate = selectedMarket.endDate || eventData?.endDate || null;
    const intervalMs = (() => {
      const marketSlug = selectedMarket.slug || slug || "";
      if (marketSlug.includes("-5m-")) return 5 * 60 * 1000;
      if (marketSlug.includes("-15m-")) return 15 * 60 * 1000;
      if (marketSlug.includes("-1h-")) return 60 * 60 * 1000;
      if (marketSlug.includes("-4h-")) return 4 * 60 * 60 * 1000;
      return 15 * 60 * 1000;
    })();
    const eventStartTime = resolvedEndDate
      ? new Date(new Date(resolvedEndDate).getTime() - intervalMs).toISOString()
      : null;
    const expired = resolvedEndDate
      ? Date.now() >= new Date(resolvedEndDate).getTime()
      : !!selectedMarket.closed;

    return {
      ...selectedMarket,
      image: selectedMarket.image || eventData?.image || null,
      endDate: resolvedEndDate,
      eventStartTime: selectedMarket.eventStartTime ?? eventStartTime,
      active: !expired,
      closed: expired,
      acceptingOrders: !expired && !eventData?.closed,
      priceToBeat: selectedMarket.priceToBeat ?? null,
      volume24hr: 0,
      parentEventSlug: selectedMarket.slug || eventData?.slug || null,
      competitive: null,
      eventCategory: getPublicEventCategory(eventData?.tags),
    };
  }, [selectedMarketLooksLikePolymarketCrypto, selectedMarket, eventData?.endDate, eventData?.closed, eventData?.image, eventData?.slug, eventData?.tags, slug]);
  const resolvedPolymarketCryptoMarket = useMemo(() => {
    if (!selectedMarketLooksLikePolymarketCrypto) return null;
    const enrichedMarket = selectedMarketDetails?.market ?? null;
    const fallbackMarket = fallbackPolymarketCryptoMarket ?? null;
    const targetEndDate = selectedMarket?.endDate || eventData?.endDate || null;

    const marketMatchesEventWindow = (() => {
      if (!enrichedMarket) return false;
      if (selectedMarket?.slug && enrichedMarket.slug === selectedMarket.slug) return true;
      if (!targetEndDate || !enrichedMarket.endDate) return false;
      return Math.abs(new Date(enrichedMarket.endDate).getTime() - new Date(targetEndDate).getTime()) < 60_000;
    })();

    const market = marketMatchesEventWindow ? enrichedMarket : fallbackMarket;
    if (!market) return null;

    return {
      ...market,
      image: market.image ?? null,
      endDate: market.endDate ?? null,
      eventStartTime: market.eventStartTime ?? null,
      priceToBeat: market.priceToBeat ?? null,
      volume24hr: market.volume24hr ?? 0,
      oneDayPriceChange: market.oneDayPriceChange ?? null,
      oneHourPriceChange: market.oneHourPriceChange ?? null,
      competitive: market.competitive ?? null,
      parentEventSlug: market.parentEventSlug ?? null,
      conditionId: market.conditionId ?? null,
      eventCategory: getPublicEventCategory(eventData?.tags, market.eventCategory ?? null),
    };
  }, [selectedMarketLooksLikePolymarketCrypto, selectedMarketDetails?.market, fallbackPolymarketCryptoMarket, selectedMarket?.endDate, eventData?.endDate]);
  // Polymarket crypto up/down expiration: flip `polymarketCryptoExpired`
  // when `endDate` is reached so the resolved-market UI surfaces in
  // place. Earlier iterations of this block also auto-redirected to
  // the next live event in the series — that was added as evasion for
  // a React #300 crash at expiration. Root cause was fixed in
  // `EventMarketsGrid.tsx:319` (three hooks lived below an early
  // return) so the redirect was removed; users now stay on the
  // resolved event and decide where to go next.
  const [polymarketCryptoExpired, setPolymarketCryptoExpired] = useState(false);
  useEffect(() => {
    const endDate = resolvedPolymarketCryptoMarket?.endDate;
    if (!endDate) {
      setPolymarketCryptoExpired(false);
      return;
    }

    const endMs = new Date(endDate).getTime();
    const checkExpired = () => setPolymarketCryptoExpired(Date.now() >= endMs);

    checkExpired();
    const intervalId = setInterval(checkExpired, 1000);
    return () => clearInterval(intervalId);
  }, [resolvedPolymarketCryptoMarket?.endDate]);

  const effectiveResolvedPolymarketCryptoMarket = useMemo(() => {
    if (!resolvedPolymarketCryptoMarket) return null;
    if (!polymarketCryptoExpired) return resolvedPolymarketCryptoMarket;

    return {
      ...resolvedPolymarketCryptoMarket,
      active: false,
      closed: true,
      acceptingOrders: false,
    };
  }, [resolvedPolymarketCryptoMarket, polymarketCryptoExpired]);

  const resolvedPolymarketCryptoMarketEnded = effectiveResolvedPolymarketCryptoMarket
    ? (!effectiveResolvedPolymarketCryptoMarket.acceptingOrders || effectiveResolvedPolymarketCryptoMarket.closed)
    : false;
  const resolvedPolymarketCryptoHeroData = effectiveResolvedPolymarketCryptoMarket
    ? {
        slug: effectiveResolvedPolymarketCryptoMarket.slug,
        question: effectiveResolvedPolymarketCryptoMarket.question,
        image: effectiveResolvedPolymarketCryptoMarket.image || null,
        endDate: effectiveResolvedPolymarketCryptoMarket.endDate || null,
        closed: !!effectiveResolvedPolymarketCryptoMarket.closed,
        active: !!effectiveResolvedPolymarketCryptoMarket.active,
        acceptingOrders: !!effectiveResolvedPolymarketCryptoMarket.acceptingOrders,
        outcomes: effectiveResolvedPolymarketCryptoMarket.outcomes || ["Yes", "No"],
        outcomePrices: effectiveResolvedPolymarketCryptoMarket.outcomePrices || [0.5, 0.5],
        volume: effectiveResolvedPolymarketCryptoMarket.volume || 0,
        volume24hr: effectiveResolvedPolymarketCryptoMarket.volume24hr || 0,
        liquidity: effectiveResolvedPolymarketCryptoMarket.liquidity || 0,
        oneDayPriceChange: effectiveResolvedPolymarketCryptoMarket.oneDayPriceChange ?? null,
        parentEventSlug: effectiveResolvedPolymarketCryptoMarket.parentEventSlug || null,
        competitive: effectiveResolvedPolymarketCryptoMarket.competitive ?? null,
        conditionId: effectiveResolvedPolymarketCryptoMarket.conditionId || null,
        eventCategory: effectiveResolvedPolymarketCryptoMarket.eventCategory || null,
      }
    : null;
  const isPolymarketCryptoMarketEvent = source === 'polymarket'
    && !!effectiveResolvedPolymarketCryptoMarket
    && (
      effectiveResolvedPolymarketCryptoMarket.slug?.includes('updown')
      || (effectiveResolvedPolymarketCryptoMarket.question || '').toLowerCase().includes('up or down')
    );
  const effectiveEventClosed = !!(eventData?.closed || (isPolymarketCryptoMarketEvent && polymarketCryptoExpired));

  // PM "awaiting resolution" fast-flip. A market sits closed=true on gamma
  // but outcomePrices stay mid-range until the oracle pushes final 1/0.
  // useEventQuery's refetchInterval can't catch this for Polymarket: the
  // proxy at /api/polymarket/event/[slug] caches Gamma for 5min, so a plain
  // refetch returns the stale blob until the TTL expires (~5min). We hit the
  // proxy with ?fresh=1 (it skips cache read+write) every 5s and write the
  // result to the LIVE query key.
  //
  // 2026-05-29 — two fixes vs the prior version of this loop: (1) the key now
  // includes `locale` to match useEventQuery's ["event-page", source, slug,
  // locale] — the old 3-element key was dead (nothing read it). (2) the
  // refetchInterval in useEventQuery is now gated OFF for polymarket (this
  // loop owns PM limbo), so there's no double-poll. Shape is safe: PM
  // fetchEventData is `return response.json()` from the same endpoint, so the
  // raw ?fresh=1 json is byte-identical to what the page reads.
  //
  // Polymarket-only — Kalshi/Predict keep useEventQuery's refetchInterval
  // (their sole resolution mechanism); their proxies/flows differ.
  const queryClient = useQueryClient();
  const eventLocale = useLocale();
  useEffect(() => {
    if (source !== "polymarket") return;
    if (!effectiveEventClosed) return;
    const hasWinner = (eventData?.markets || []).some((m) => {
      const yes = Number(m.outcomePrices?.[0] ?? 0);
      const no = Number(m.outcomePrices?.[1] ?? 0);
      return yes >= 0.99 || no >= 0.99;
    });
    if (hasWinner) return; // resolution complete, nothing to poll for

    let cancelled = false;
    const ctrl = new AbortController();
    const tick = async () => {
      try {
        const res = await fetch(`/api/polymarket/event/${slug}?fresh=1`, {
          cache: "no-store",
          signal: ctrl.signal,
        });
        if (!res.ok || cancelled) return;
        const fresh = await res.json();
        if (cancelled) return;
        // Key MUST match useEventQuery (incl. locale) or the write lands in a
        // dead entry the page never reads.
        queryClient.setQueryData(["event-page", source, slug, eventLocale], fresh);
      } catch {
        // Silent — interval will retry.
      }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
      ctrl.abort();
    };
  }, [effectiveEventClosed, eventData?.markets, queryClient, source, slug, eventLocale]);

  const effectiveEventData = useMemo(() => {
    if (!eventData) return eventData;
    if (!effectiveEventClosed || (eventData.closed && eventData.active === false)) return eventData;
    return {
      ...eventData,
      closed: true,
      active: false,
    };
  }, [eventData, effectiveEventClosed]);
  const effectiveSelectedMarket = useMemo(() => {
    if (!selectedMarket) return selectedMarket;
    if (!isPolymarketCryptoMarketEvent || !effectiveResolvedPolymarketCryptoMarket) return selectedMarket;
    return {
      ...selectedMarket,
      ...effectiveResolvedPolymarketCryptoMarket,
    };
  }, [selectedMarket, isPolymarketCryptoMarketEvent, effectiveResolvedPolymarketCryptoMarket]);

  // Cross-platform Kalshi match lookup hoisted to the top-level so the
  // hook order stays stable across renders. Previously the hook lived
  // inside an IIFE wrapped in `{effectiveSelectedMarket && ...}` which
  // made it conditional — when the user landed on a market that briefly
  // resolved `effectiveSelectedMarket` to null, React threw "Rendered
  // fewer hooks than expected" because the hook had run on the previous
  // render but didn't on the new one. The result is consumed in the
  // MarketTalks JSX block further down.
  const matchingMarketsConditionId = source === 'polymarket'
    ? (effectiveSelectedMarket as any)?.conditionId
      || (eventData as any)?.markets?.[0]?.conditionId
      || null
    : null;
  const { data: matchingMarketsResult } = useMatchingMarkets(
    matchingMarketsConditionId,
    source === 'polymarket',
  );

  const kalshiCryptoTargetLabel = useMemo(() => {
    if (source !== "kalshi" || !cryptoInfo.isCrypto || !effectiveSelectedMarket) return null;
    const rawText = String(
      (effectiveSelectedMarket as any).groupItemTitle ||
      (effectiveSelectedMarket as any).title ||
      effectiveSelectedMarket.question ||
      ""
    );
    const strike = extractStrike(rawText);
    if (strike == null) return null;
    const formatted = new Intl.NumberFormat("en-US", {
      maximumFractionDigits: strike % 1 === 0 ? 0 : 2,
      minimumFractionDigits: 0,
    }).format(strike);
    return `$${formatted}`;
  }, [source, cryptoInfo.isCrypto, effectiveSelectedMarket]);

  // Widget config now handled by useEventWidgetConfig hook (fires in parallel)

  // AI Context Analysis - triggered for any widget type that isn't rendered directly
  // Direct types: match (MatchContextSidebar), sportLeague (SportsContextSidebar), spotify, gaming, xtracker
  // Everything else (none, generic, *_ai types, AI contextTypes like weather_forecast) needs AI
  const DIRECT_WIDGET_TYPES = new Set(["match", "spotify", "gaming", "xtracker"]);
  const shouldUseAI = widgetConfig != null && !DIRECT_WIDGET_TYPES.has(widgetConfig.widgetType) && !widgetConfig.sportLeague;

  // Memoize marketQuestions to prevent infinite re-renders
  const marketQuestions = useMemo(() =>
    eventData?.markets?.map(m => m.question) || [],
    [eventData?.markets]
  );

  const {
    analysis: aiAnalysis,
    loading: aiLoading,
  } = useAIContextAnalysis({
    eventSlug: slug,
    eventTitle: eventData?.title || "",
    eventDescription: eventData?.description || "",
    marketQuestions,
    enabled: shouldUseAI && !!eventData,
  });

  // Network activity - friends/leaders/watchlist who traded these markets
  const marketSlugs = useMemo(() =>
    eventData?.markets?.map(m => m.slug) || [],
    [eventData?.markets]
  );
  const { activity: networkActivity, loading: networkLoading } = useNetworkActivity(marketSlugs);

  // Determine if sidebar should show based on widget type or AI analysis
  const hasContextSidebar = (widgetConfig && widgetConfig.widgetType !== "none") ||
                            (aiAnalysis && aiAnalysis.contextType !== "generic") ||
                            (shouldUseAI && aiLoading) || // Show sidebar container while AI is loading
                            (eventData && activeMarkets.length > 0); // Always show sidebar if there are active markets (GenericEventSidebar fallback)

  // Auto-detect sports MATCH based on market structure (exactly 2-3 "Will X win?" markets + optional Draw)
  // IMPORTANT: Events with many "will win" markets (like World Cup Winner with 20+ countries) are NOT matches
  const isSportsEventFromMarkets = useMemo(() => {
    if (!eventData?.markets || eventData.markets.length < 2) return false;

    // Kalshi sports detection: category Sports + binary (2 markets, no draw question)
    if (source === 'kalshi') {
      const isSports =
        ((eventData as any).category || '').toLowerCase() === 'sports' ||
        (eventData.tags || []).some((t: any) => /sport/i.test(typeof t === 'string' ? t : t?.label || ''));
      return isSports && eventData.markets.length >= 2 && eventData.markets.length <= 3;
    }

    // Check if we have markets with "Will X win?" pattern
    const winMarkets = eventData.markets.filter(m =>
      m.question?.toLowerCase().includes('will') && m.question?.toLowerCase().includes('win')
    );

    // Check if there's a draw market
    const hasDrawMarket = eventData.markets.some(m =>
      m.question?.toLowerCase().includes('draw')
    );

    // A real match has exactly 2-3 "will win" markets (Team A, Team B, maybe a third)
    // If we have 4+ win markets, it's a tournament/championship with multiple outcomes (NOT a match)
    const isRealMatch = winMarkets.length >= 2 && winMarkets.length <= 3;

    // Or if we have 1-2 win markets + draw market, it's also a real match
    const isMatchWithDraw = winMarkets.length >= 1 && winMarkets.length <= 2 && hasDrawMarket;

    return isRealMatch || isMatchWithDraw;
  }, [eventData?.markets]);

  // Determine if this is a sports match view
  // NOTE: widgetConfig is ONLY for the sidebar context (ESPN scores, etc.)
  // The main content view is determined ONLY by market structure, not widget type
  const isSportsMatchView = isSportsEventFromMarkets;
  // Live status used to default the chart timeframe to 1H for in-progress matches
  const { data: kalshiLiveSet } = useKalshiLiveTickers(source === 'kalshi');
  const eventTickerForLive = ((eventData as any)?.eventTicker || (eventData as any)?.id || slug || '').toUpperCase();
  const isKalshiLive = source === 'kalshi' && !!kalshiLiveSet?.has(eventTickerForLive);
  // Polymarket sports events expose `live:true` directly on eventData (from Gamma)
  const isPolymarketLive = source === 'polymarket' && (eventData as any)?.live === true;
  const isEventLive = isKalshiLive || isPolymarketLive;

  // Centralized real-time price hook (Polymarket only — Kalshi/Predict have their own)
  // IMPORTANT: lastUpdate triggers re-renders when prices change
  const { getMarketPrice: getRealtimePrice, getBuyPrices, isConnected: wsConnected, lastUpdate } = useRealtimeMarketPrice({
    markets: source === 'polymarket' && !eventData?.closed ? (eventData?.markets || []) : [],
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _priceUpdateTrigger = lastUpdate;

  // DFlow real-time prices for Kalshi markets
  const kalshiMarketTickers = useMemo(() => {
    if (source !== 'kalshi' || !eventData?.markets) return [];
    return eventData.markets.map((m) => getKalshiTicker(m)).filter(Boolean);
  }, [source, eventData?.markets, getKalshiTicker]);

  const { getPrice: getDFlowPrice, prices: dflowPrices } = useDFlowRealtimePrice({
    tickers: kalshiMarketTickers,
    enabled: source === 'kalshi' && !eventData?.closed && kalshiMarketTickers.length > 0,
  });

  // Predict.fun real-time prices
  const predictMarketIds = useMemo(() => {
    if (source !== 'predict' || !eventData?.markets) return [];
    return eventData.markets.filter((m) => m.active && !m.closed).map((m) => m.id).filter(Boolean);
  }, [source, eventData?.markets]);

  const { getPrice: getPredictPrice } = usePredictRealtimePrice({
    marketIds: predictMarketIds,
    enabled: source === 'predict' && !eventData?.closed && predictMarketIds.length > 0,
  });

  // Hyperliquid live mid prices. Each HL inner market has YES (asset_id ≡
  // market.id) and NO (asset_id+1). We seed both into the WS hook so the
  // YES-side and NO-side ticks populate independently.
  const hyperliquidAssetIds = useMemo(() => {
    if (source !== 'hyperliquid' || !eventData?.markets) return [];
    const ids: number[] = [];
    for (const m of eventData.markets) {
      const yesId = Number((m as any).id);
      if (Number.isFinite(yesId) && yesId > 0) {
        ids.push(yesId);
        // NO-side asset_id is YES + 1 in HIP-4's outcomeAssetId encoding.
        ids.push(yesId + 1);
      }
    }
    return ids;
  }, [source, eventData?.markets]);

  const { getPrice: getHyperliquidPrice } = useHyperliquidRealtimePrice({
    assetIds: hyperliquidAssetIds,
    enabled: source === 'hyperliquid' && !eventData?.closed && hyperliquidAssetIds.length > 0,
  });

  // Wrappers: source-specific live prices
  const getLivePrice = useCallback(
    (market: MarketLike): [number, number] => {
      if (source === 'kalshi') {
        const ticker = getKalshiTicker(market);
        const live = ticker ? getDFlowPrice(ticker) : undefined;
        if (live) {
          const mid = (bid: number | null, ask: number | null, fb: number) => {
            if (bid !== null && ask !== null) return (bid + ask) / 2;
            return bid ?? ask ?? fb;
          };
          return [
            mid(live.yesBid, live.yesAsk, market.outcomePrices?.[0] as number ?? 0.5),
            mid(live.noBid, live.noAsk, market.outcomePrices?.[1] as number ?? 0.5),
          ];
        }
      }
      if (source === 'predict') {
        const live = getPredictPrice((market as Market).id);
        if (live?.midPrice != null) {
          return [live.midPrice, 1 - live.midPrice];
        }
      }
      // Hyperliquid: prefer the WS tick keyed by asset_id; fall back to the
      // SSR snapshot when the socket hasn't delivered the first tick yet
      // (or for asset_ids the catalog poller hasn't seen recently).
      if (source === 'hyperliquid') {
        const yesId = Number((market as any).id);
        const yesTick = Number.isFinite(yesId) ? getHyperliquidPrice(yesId) : undefined;
        const noTick = Number.isFinite(yesId) ? getHyperliquidPrice(yesId + 1) : undefined;
        const yes = yesTick?.mid != null
          ? yesTick.mid
          : Number(market.outcomePrices?.[0] ?? 0.5);
        // NO mid usually mirrors `1 - YES` on a binary, but if HL emitted
        // an explicit NO tick (different mid because of one-sided order
        // flow at that millisecond), trust it.
        const no = noTick?.mid != null
          ? noTick.mid
          : Math.max(0, Math.min(1, 1 - yes));
        return [yes, no];
      }
      return getRealtimePrice(market);
    },
    [source, getDFlowPrice, getPredictPrice, getRealtimePrice, getKalshiTicker, getHyperliquidPrice]
  );

  const getLiveBuyPrices = useCallback(
    (market: MarketLike) => {
      if (source === 'kalshi') {
        const ticker = getKalshiTicker(market);
        const live = ticker ? getDFlowPrice(ticker) : undefined;
        if (live) {
          const yFb = market.outcomePrices?.[0] as number ?? 0.5;
          const nFb = market.outcomePrices?.[1] as number ?? 0.5;
          return {
            buyYes: live.yesAsk ?? live.yesBid ?? yFb,
            buyNo: live.noAsk ?? live.noBid ?? nFb,
            hasRealBuyYes: live.yesAsk !== null,
            hasRealBuyNo: live.noAsk !== null,
            isRealOrderbook: live.yesBid !== null || live.yesAsk !== null,
          };
        }
      }
      if (source === 'predict') {
        const live = getPredictPrice((market as Market).id);
        if (live) {
          const yFb = market.outcomePrices?.[0] as number ?? 0.5;
          const nFb = market.outcomePrices?.[1] as number ?? 0.5;
          return {
            buyYes: live.bestAsk ?? live.midPrice ?? yFb,
            buyNo: live.bestBid != null ? 1 - live.bestBid : (live.midPrice != null ? 1 - live.midPrice : nFb),
            hasRealBuyYes: live.bestAsk !== null,
            hasRealBuyNo: live.bestBid !== null,
            isRealOrderbook: live.bestBid !== null || live.bestAsk !== null,
          };
        }
      }
      if (source === 'hyperliquid') {
        const yesId = Number((market as any).id);
        const yesTick = Number.isFinite(yesId) ? getHyperliquidPrice(yesId) : undefined;
        const noTick = Number.isFinite(yesId) ? getHyperliquidPrice(yesId + 1) : undefined;
        const yFb = yesTick?.mid != null
          ? yesTick.mid
          : Number(market.outcomePrices?.[0] ?? 0.5);
        const nFb = noTick?.mid != null
          ? noTick.mid
          : Math.max(0, Math.min(1, 1 - yFb));
        const hasYes = yesTick?.mid != null;
        const hasNo = noTick?.mid != null;
        return {
          buyYes: yFb,
          buyNo: nFb,
          // Treat the live mid as a real quote for UI sanity (no "?" badge);
          // we don't have ask/bid yet — that needs an l2Book WS sub which
          // we'll layer on when per-market detail tabs ship.
          hasRealBuyYes: hasYes,
          hasRealBuyNo: hasNo,
          isRealOrderbook: hasYes || hasNo,
        };
      }
      return getBuyPrices(market);
    },
    [source, getDFlowPrice, getPredictPrice, getBuyPrices, getKalshiTicker, getHyperliquidPrice]
  );

  // Format volume
  const formatVolume = (vol: number | string | null | undefined) => {
    const n = Number(vol);
    if (vol == null || isNaN(n)) return "$0";
    if (n >= 1_000_000) {
      return `$${(n / 1_000_000).toFixed(2)}M`;
    } else if (n >= 1_000) {
      return `$${(n / 1_000).toFixed(2)}K`;
    }
    return `$${n.toFixed(2)}`;
  };

  if (loading) {
    return (
      <main className="min-h-screen">
        <div className="w-full pt-24 pb-12">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 dark:bg-[#18181D] rounded w-3/4" />
            <div className="h-96 bg-gray-200 dark:bg-[#18181D] rounded border border-gray-300 dark:border-[#1A1A1A]" />
          </div>
        </div>
      </main>
    );
  }

  if (error || !eventData) {
    return (
      <main className="min-h-screen bg-white dark:bg-[#0f0f12]">
        <div className="flex flex-col items-center justify-center min-h-[70vh] px-4 text-center">
          {/* Sad face emoji */}
          <div className="text-8xl mb-6">😢</div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4 font-satoshi">
            Oops, fren!
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-gray-600 dark:text-white mb-2 font-inter-tight">
            Market not found
          </p>

          {/* Description */}
          <p className="text-base text-gray-500 dark:text-gray-400 mb-8 max-w-md font-inter-tight">
            This prediction market doesn&apos;t exist or may have been removed from {source === 'kalshi' ? 'Kalshi' : 'Polymarket'}
          </p>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={sourceUrl("/markets", source as Source)}
              className="px-8 py-3 bg-white text-black font-bold rounded-[5px] hover:bg-white/90 transition-colors text-base font-inter-tight"
            >
              Explore Markets
            </a>
            <button
              onClick={() => window.history.back()}
              className="px-8 py-3 bg-gray-200 dark:bg-[#18181D] text-gray-700 dark:text-white font-medium rounded-[5px] hover:bg-gray-300 dark:hover:bg-[#25252B] transition-colors text-base font-inter-tight"
            >
              Go Back
            </button>
          </div>
        </div>
      </main>
    );
  }

  const resolvedEventData = effectiveEventData ?? eventData;
  const isActive = resolvedEventData.active && !resolvedEventData.closed;

  return (
    <main className="min-h-screen">
      {/* Mobile bottom padding sized for: MobileBottomNav (52px) +
          sticky CTA (~50px) + safe-area. ~pb-28 (112px) covers the
          worst case and avoids the empty 200px gap the previous pb-36
          left under the FAQs. Desktop unchanged. */}
      <div className="w-full pt-2 md:pt-4 pb-28 lg:pb-12">
        {/* 2-track grid: fluid main + a proportional-but-bounded trading-
            panel column. Was lg:grid-cols-12 with the panel at col-span-3
            + min-w-[300px]; the min-width forced it to overflow and mount
            over the chart when the app sidebar was expanded (2026-05-28).
            A flat 320px track fixed that but left the panel too narrow on
            wide screens (the old col-span-3 was ~25%). clamp(300,25%,480)
            restores the proportional width — ~25% like before, capped at
            480px so it doesn't bloat on ultrawide — while the 300px floor
            + minmax(0,1fr) main guarantee it can never overflow. */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_clamp(300px,25%,480px)] gap-4">
          <div className="min-w-0">

        {/* DexTools landing co-branding — full-width bar in the main column,
            OUTSIDE the hero card, self-gated (only on a real DexTools arrival). */}
        <DextoolsLandingBar fullWidth />

        {/* Hero + Chart Card */}
        <div ref={heroRef} className={`mb-3 md:mb-4 ${source === 'kalshi' && isSportsMatchView ? '' : 'bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden'}`}>
          <EventHeroChart
            source={source}
            isSportsMatchView={isSportsMatchView}
            resolvedEventData={resolvedEventData}
            eventData={eventData}
            slug={slug}
            formatVolume={formatVolume}
            isActive={isActive}
            activeMarketsCount={activeMarkets.length}
            isPolymarketCryptoMarketEvent={isPolymarketCryptoMarketEvent}
            effectiveResolvedPolymarketCryptoMarket={effectiveResolvedPolymarketCryptoMarket}
            resolvedPolymarketCryptoHeroData={resolvedPolymarketCryptoHeroData}
            resolvedPolymarketCryptoMarketEnded={resolvedPolymarketCryptoMarketEnded}
            selectedMarketLooksLikePolymarketCrypto={selectedMarketLooksLikePolymarketCrypto}
            cryptoInfo={cryptoInfo}
            cryptoCloseDate={cryptoCloseDate}
            switchToNextCryptoEvent={switchToNextCryptoEvent}
            currentCryptoPrice={currentCryptoPrice}
            s3Open6H={s3Open6H}
            kalshiCryptoTargetLabel={kalshiCryptoTargetLabel}
            pmPriceLevelInfo={pmPriceLevelInfo}
            isDeadlineEvent={isDeadlineEvent}
            hlPriceLevelInfo={hlPriceLevelInfo}
            selectedMarket={selectedMarket}
            chartMarkets={chartMarkets}
            dflowPrices={dflowPrices}
            isEventLive={isEventLive}
            initialCryptoCandles={initialCryptoCandles}
          />

        {/* Sports Match View — Polymarket only; Kalshi sports use KalshiSportsHero */}{/* close-heroRef-block-marker */}
        {isSportsMatchView && source !== 'kalshi' && resolvedEventData.markets.length >= 2 && (
          <SportsMatchView
            markets={resolvedEventData.markets as Market[]}
            eventData={resolvedEventData}
            source={source}
            slug={slug}
            widgetConfig={widgetConfig}
            getLivePrice={getLivePrice}
            getLiveBuyPrices={getLiveBuyPrices}
            openTradingModal={openTradingModal}
            formatVolume={formatVolume}
          />
        )}

        {/* Markets List.
            Polymarket sports matches hide the grid because SportsMatchView
            already draws the two-team outcomes row with its own YES/NO
            buttons. Kalshi sports matches only draw the hero (score + logos)
            — they still need the outcome rows with buttons underneath. */}
        <div className={`${isSportsMatchView && source !== 'kalshi' ? "hidden" : ""}`}>
          {source === 'polymarket' && (
            <div className="mb-3">
              <EventRewardsBadge
                markets={(cryptoInfo.isCrypto ? displayedActiveMarkets : resolvedEventData.markets) as Market[]}
              />
            </div>
          )}
          <EventMarketsGrid
            markets={
              shouldFilterStrikes
                ? (displayedActiveMarkets as Market[])
                : (activeMarkets.length > 0
                  ? (activeMarkets as Market[])
                  : (resolvedEventData.markets as Market[]))
            }
            source={source}
            eventData={resolvedEventData}
            selectedMarketId={selectedMarketId}
            selectedMarket={effectiveSelectedMarket as Market | null}
            initialOutcome={urlOutcome}
            showAllMarkets={shouldFilterStrikes || (cryptoInfo.isCrypto && currentCryptoPrice != null) ? showAllCryptoMarkets : showAllMarkets}
            setShowAllMarkets={shouldFilterStrikes || (cryptoInfo.isCrypto && currentCryptoPrice != null) ? setShowAllCryptoMarkets : setShowAllMarkets}
            expandedMarketId={expandedMarketId}
            hideAdvancedTabs={pmPriceLevelInfo.isPriceLevel}
            onSelectMarket={(id) => {
              // Row click should keep two behaviours in parallel:
              //   a) selection on the right panel is STICKY — expanding a
              //      row promotes it to selected; collapsing doesn't
              //      un-select (right panel stays on that market).
              //   b) the inline OutcomeDetailPanel still toggles — click
              //      a row once to expand, click again to collapse.
              // The grid falls back to `selectedMarketId` when
              // `expandedMarketId` is null, which would keep the accordion
              // visually open after an explicit collapse. We track that
              // intent via userCollapsedMarketId so the grid can exclude
              // the fallback for the specific row the user just closed.
              const isCollapsing = expandedMarketId === id;
              setExpandedMarketId(isCollapsing ? null : id);
              setSelectedMarketId(id);
              setUserCollapsedMarketId(isCollapsing ? id : null);
              syncUrlMarket(id);
              // Mobile: clicking the row only expands the inline accordion.
              // The mobile trade sheet is opened ONLY when the user taps the
              // YES / NO buttons (those call openTradingModal directly).
            }}
            userCollapsedMarketId={userCollapsedMarketId}
            getLivePrice={getLivePrice}
            getLiveBuyPrices={getLiveBuyPrices}
            openTradingModal={openTradingModal}
            onSelectOutcome={handleSelectOutcome}
            onSelectMarketMobile={(id) => {
              // Tap on a market card body in the mobile layout — open the
              // full-screen detail overlay. Also sync the regular sidebar
              // selection so the desktop state stays in sync if the user
              // rotates / resizes the viewport while the overlay is open.
              setMobileDetailMarketId(id);
              setSelectedMarketId(id);
              syncUrlMarket(id);
            }}
            formatVolume={formatVolume}
            networkActivity={networkActivity}
            getTradersForMarket={getTradersForMarket}
            currentCryptoPrice={currentCryptoPrice}
            currentStrikeMarketId={currentStrikeMarketIndex != null ? activeMarkets[currentStrikeMarketIndex]?.id : undefined}
            totalMarketsCount={cryptoInfo.isCrypto ? activeMarkets.length : undefined}
          />
        </div>
        </div>

        {/* Below outcomes — separate cards */}
        {effectiveSelectedMarket && (
          <div className="space-y-3 mt-3">
            {/* Mobile-only My Positions block — desktop renders the same
                widget inside EventSidebar (right column). Sits right
                after the outcomes grid so phone users see their exposure
                without having to scroll past Market Talks first.
                HL not supported (see EventSidebar comment). */}
            {source !== 'hyperliquid' && (
              <EventMyPositions
                className="lg:hidden"
                selectedMarket={effectiveSelectedMarket as Market}
                activeMarkets={activeMarkets as Market[]}
                source={source}
                isKalshi={isKalshi}
                eventData={resolvedEventData}
                getLivePrice={getLivePrice}
                onRequestSell={requestSellInPanel}
              />
            )}
            {/* Per-outcome detail tabs render INLINE inside EventMarketsGrid
                as an accordion row under the selected market. See
                OutcomeDetailPanel.tsx + EventMarketsGrid.tsx. */}
            {source === 'kalshi' && isSportsMatchView && (
              <>
                <KalshiRelatedMarketsCard
                  eventTicker={slug}
                  onSelectMoneylineMarket={(ticker) => { setSelectedMarketId(ticker); syncUrlMarket(ticker); }}
                  selectedMarketId={selectedMarketId}
                />
                {/*
              <div className="bg-white dark:bg-[#141418] rounded-[5px] border border-gray-100 dark:border-[#1e1e24] overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-inter-tight text-[13px] text-gray-700 dark:text-gray-300 truncate">Moneyline</span>
                    </div>
                    <span className="font-inter-tight text-[10px] text-gray-400">
                      {formatVolume(
                        kalshiSportsMoneylineMarkets.reduce((sum, market) => sum + (Number(market.volume) || 0), 0)
                      )} Vol.
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-end flex-shrink-0">
                  {kalshiSportsMoneylineMarkets.map((market, idx) => {
                    const label = String((market as any).title || market.question || '');
                    const { buyYes } = getLiveBuyPrices(market);
                    const isSelected = effectiveSelectedMarket?.id === market.id;
                    const isGreen = idx === 0;
                    const isOpen = market.active && !market.closed;
                    const displayPrice = ((buyYes ?? 0) * 100);
                    return (
                      <button
                        key={market.id}
                        onClick={() => {
                          setSelectedMarketId(market.id);
                          if (isOpen) {
                            openTradingModal({
                              isOpen: true,
                              marketSlug: market.slug || market.id,
                              outcome: "YES",
                              outcomePrice: buyYes,
                              marketQuestion: market.question,
                              actualOutcome: label,
                            });
                          }
                        }}
                        className={`min-w-[100px] flex flex-col items-center gap-0.5 px-3 py-2 rounded-[5px] font-inter-tight border transition-colors ${
                          isSelected
                            ? isGreen
                              ? "border-emerald-400 dark:border-[#00FF9D]/40 bg-emerald-50 dark:bg-[#00FF9D]/[0.08] text-emerald-700 dark:text-[#00FF9D] font-semibold"
                              : "border-red-400 dark:border-[#FF1744]/40 bg-red-50 dark:bg-[#FF1744]/[0.08] text-red-700 dark:text-[#FF1744] font-semibold"
                            : "border-gray-200 dark:border-[#1e1e24] bg-gray-50 dark:bg-[#1a1a22] hover:bg-gray-100 dark:hover:bg-[#22222a]"
                        } ${!isOpen ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        <span className={`text-[11px] truncate max-w-full ${isSelected ? "" : "text-gray-500 dark:text-gray-400"}`}>
                          {label}
                        </span>
                        <span className={`font-monument text-[15px] ${isGreen ? "text-emerald-600 dark:text-[#00FF9D]" : "text-red-600 dark:text-[#FF1744]"}`}>
                          {displayPrice.toFixed(displayPrice % 1 === 0 ? 0 : 1)}¢
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
                */}
              </>
            )}

            {/* Predict Tweet Tracker — only for TWEET_COUNT events */}
            {source === 'predict' && resolvedEventData.marketVariant === 'TWEET_COUNT' && (
              <PredictTweetTracker slug={slug} />
            )}

            {/* Smart Money — Polymarket only. Moved here from the card footer
                badge: a dedicated box below the outcomes (before Market Talks)
                showing what top-performing wallets are doing. Excludes the
                short-lived crypto up/down series (no meaningful smart signal).
                Renders nothing when there's no qualifying signal. */}
            {source === 'polymarket' && !slug?.match(/-(?:5m|15m|1h|4h)-/) && (
              <EventSmartMoney
                conditionId={(effectiveSelectedMarket as any)?.conditionId}
                markets={(activeMarkets.length > 0 ? activeMarkets : resolvedEventData.markets)
                  .filter((m: any) => !!m.conditionId)
                  .map((m: any) => ({
                    conditionId: m.conditionId as string,
                    title: m.groupItemTitle || m.question || undefined,
                    image: m.imageUrl || m.image || undefined,
                  }))}
              />
            )}

            {/* Market Talks — keyed to the EVENT, not the selected outcome.
                Comments live at the event level (Polymarket passes eventId
                to the backend), so switching between outcomes inside the same
                event must NOT re-fetch or re-mount the component.
                Hyperliquid has no comments backend (no eventId/seriesId/ticker
                to key on), so the thread is always empty there — hide it. */}
            {source !== 'hyperliquid' && (
            <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A]">
              {/* Pass BOTH eventId and seriesId: short-lived events (crypto
                  updown 5m/15m, tennis match qualifications) almost never
                  have event-level comments — the conversation lives at the
                  series level (e.g. btc-up-or-down-5m series 10684). */}
              {(() => {
                // Cross-platform comments via Predexon: read the
                // `useMatchingMarkets` result we hoisted to the top of
                // the component. The hook itself MUST stay outside this
                // IIFE — calling it here makes it conditional on
                // `effectiveSelectedMarket`, which fluctuates and breaks
                // the hook-order invariant.
                const kalshiMarketTicker = matchingMarketsResult?.matches?.KALSHI?.exact?.[0]?.market_ticker || null;
                // Kalshi market ticker = "<series>-<event>-<market>"; the
                // social timeline endpoint wants the event ticker — drop
                // the trailing market segment.
                const kalshiEventFromMatch = kalshiMarketTicker
                  ? kalshiMarketTicker.split('-').slice(0, -1).join('-') || null
                  : null;

                const resolvedKalshiEventTicker = source === 'kalshi'
                  ? (resolvedEventData as any)?.eventTicker || slug
                  : kalshiEventFromMatch || undefined;

                return (
              <MarketTalks
                marketSlug={resolvedEventData.slug}
                marketQuestion={resolvedEventData.title}
                marketSource={source}
                eventId={source === 'polymarket' ? resolvedEventData.id : undefined}
                seriesId={source === 'polymarket' ? (resolvedEventData as any)?.series?.[0]?.id : undefined}
                predictCategoryId={source === 'predict' ? slug : undefined}
                kalshiEventTicker={resolvedKalshiEventTicker}
                selectedMarket={effectiveSelectedMarket ? {
                  slug: effectiveSelectedMarket.slug,
                  question: (effectiveSelectedMarket as any).groupItemTitle || effectiveSelectedMarket.question,
                  image: effectiveSelectedMarket.image,
                } : null}
                pmTokenIdToOutcome={source === 'polymarket' ? pmTokenIdToOutcome : undefined}
              />
                );
              })()}
            </div>
            )}

            {/* FrenFlow Analysis */}
              <MarketRelatedArticles
              articles={clientArticles}
              marketTitle={resolvedEventData.title}
            />

            {/* Resolution Details / Market Rules */}
            <ResolutionDetails
              source={source}
              eventData={resolvedEventData}
              selectedMarketDetails={selectedMarketDetails}
              selectedMarket={effectiveSelectedMarket as Market | null}
            />

            {/* FAQs */}
            {faqs.length > 0 && (
              <div
                className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-4 md:p-6"
                onMouseEnter={() => setFaqHovered(true)}
                onMouseLeave={() => setFaqHovered(false)}
              >
                <div className="flex items-center gap-2 mb-1">
                  <BookOpenTextIcon size={20} className="text-gray-400 dark:text-gray-500" isHovered={faqHovered} />
                  <h3 className="font-monument text-base md:text-lg text-gray-900 dark:text-white">{t("section.faq")}</h3>
                </div>
                <p className="font-inter-tight text-xs text-gray-500 dark:text-gray-400 mb-4">{t("section.faqSubtitle")}</p>
                <div className="divide-y divide-gray-100 dark:divide-[#1A1A1A]">
                  {faqs.map((faq, i) => (
                    <details key={i} className="group">
                      <summary className="flex items-start gap-3 cursor-pointer list-none py-3.5 px-1 -mx-1 rounded-[5px] hover:bg-gray-50 dark:hover:bg-[#0f0f12]/60 transition-colors">
                        <svg className="w-4 h-4 mt-0.5 text-gray-400 dark:text-gray-500 shrink-0 transition-transform duration-200 group-open:rotate-90 group-open:text-gray-600 dark:group-open:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.25}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                        <span className="font-inter-tight text-sm font-semibold text-gray-900 dark:text-white leading-snug">{faq.question}</span>
                      </summary>
                      <div className="pl-7 pr-1 pb-4 pt-1">
                        <FaqAnswerBody answer={faq.answer} />
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
          </div>

          {/* Right Sidebar + Context Overlay */}
          {effectiveSelectedMarket && (
            <EventSidebar
              selectedMarket={effectiveSelectedMarket as Market}
              source={source}
              isKalshi={isKalshi}
              eventData={resolvedEventData}
              activeMarkets={activeMarkets as Market[]}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              alertModalOpen={alertModalOpen}
              setAlertModalOpen={setAlertModalOpen}
              hasContextSidebar={!!hasContextSidebar}
              widgetConfig={widgetConfig}
              shouldUseAI={shouldUseAI}
              aiAnalysis={aiAnalysis}
              aiLoading={aiLoading}
              getLivePrice={getLivePrice}
              getLiveBuyPrices={getLiveBuyPrices}
              openTradingModal={openTradingModal}
              getRealtimePrice={getRealtimePrice}
              getBuyPrices={getBuyPrices}
              onSelectMarket={(id) => { setSelectedMarketId(id); syncUrlMarket(id); }}
              initialOutcomeIndex={selectedOutcomeIndex}
              initialMode={panelSellSeed ? 'sell' : undefined}
              seedOwnedPosition={panelSellSeed}
              onRequestSell={requestSellInPanel}
            />
          )}
        </div>
      </div>

      {/* Mobile-only full-screen market detail overlay (Polymarket-style).
          Opens when the user taps a market card on the mobile layout
          inside EventMarketsGrid. Reuses OutcomeDetailPanel for chart /
          orderbook / trades / holders parity with the desktop accordion.
          Renders nothing on screens >=sm because the overlay itself is
          gated by `sm:hidden`. */}
      {mobileDetailMarketId && (() => {
        const market = (activeMarkets.length > 0 ? activeMarkets : resolvedEventData.markets).find(
          (m: any) => m.slug === mobileDetailMarketId || m.id === mobileDetailMarketId
        ) as Market | undefined;
        if (!market) return null;
        return (
          <MobileMarketDetailOverlay
            market={market}
            source={source}
            eventData={resolvedEventData}
            hideAdvancedTabs={pmPriceLevelInfo.isPriceLevel}
            getLivePrice={getLivePrice}
            getLiveBuyPrices={getLiveBuyPrices}
            openTradingModal={openTradingModal}
            onClose={() => setMobileDetailMarketId(null)}
          />
        );
      })()}

      {/* Mobile Bottom Sheet — only shows when user taps an outcome */}
      {selectedMarketId && mobileTradeOpen && (
        <MobileBottomSheet
          selectedMarket={effectiveSelectedMarket as Market | null}
          activeMarketsCount={activeMarkets.length}
          source={source}
          mobileTradeOpen={mobileTradeOpen}
          setMobileTradeOpen={setMobileTradeOpen}
          setAlertModalOpen={setAlertModalOpen}
          getLivePrice={getLivePrice}
          getLiveBuyPrices={getLiveBuyPrices}
          openTradingModal={openTradingModal}
          eventClosed={effectiveEventClosed}
          initialOutcomeIndex={selectedOutcomeIndex}
          activeMarkets={activeMarkets as Market[]}
          onSelectMarket={(id) => {
            setSelectedMarketId(id);
            syncUrlMarket(id);
          }}
        />
      )}

      {/* Trading modals rendered by AppLayoutWithSidebar via useTradingModalStore */}

      {/* Kalshi DFlow KYC Modal — shown only when user tries to trade without verification */}
      {showKycModal && !proofKyc.verified && (
        <>
          <div className="fixed inset-0 bg-black/70 z-[300]" onClick={() => setShowKycModal(false)} />
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
            <div className="bg-white dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] shadow-2xl p-6 w-full max-w-sm">
              <div className="flex flex-col items-center text-center gap-4">
                <div className="w-14 h-14 rounded-[5px] bg-[#09C285]/10 flex items-center justify-center">
                  <FiLock className="w-7 h-7 text-[#09C285]" />
                </div>
                <div>
                  <p className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                    Identity Verification Required
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Verify your identity to trade Kalshi markets via DFlow. Takes ~2 minutes.
                  </p>
                </div>
                <button
                  onClick={proofKyc.startVerification}
                  className="w-full px-6 py-2.5 bg-[#09C285] text-white text-sm font-semibold rounded-[5px] hover:bg-[#07a872] transition-colors"
                >
                  Verify Identity
                </button>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowKycModal(false)}
                    className="text-[11px] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <a
                    href="https://pond.dflow.net/learn/proof"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    What is DFlow Proof?
                  </a>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Sticky CTA bar removed — mobile uses EventMarketsGrid mobile
          cards (full-width Buy Yes / Buy No) plus the
          MobileMarketDetailOverlay's fixed-bottom CTA, and tablet/desktop
          rely on the in-card YES/NO buttons + EventSidebar's TradingPanel.
          The previous sticky bar duplicated those entry points. */}

      {/* Mobile sticky compact header — surfaces the event identity
          (image + title) plus copy-link / bookmark controls once the
          user has scrolled past EventHero. Lives outside <main> so it
          can stay fixed under the AppLayoutWithSidebar mobile chrome.
          The CSS below cooperates with that layout's
          `data-mobile-header-hidden` flag so the two headers slide
          together instead of stacking. */}
      <MobileEventStickyHeader
        eventData={resolvedEventData}
        source={source}
        slug={slug}
      />
      <style jsx global>{`
        .event-mobile-sticky-header {
          /* Sits below AppLayoutWithSidebar's 52px mobile header by
             default. When the layout hides that header on scroll-down,
             this surface moves up to top:0 so it stays flush instead
             of revealing a gap. Matches the layout's 300ms transition. */
          top: 52px;
          transition: top 300ms ease, transform 300ms ease, opacity 300ms ease;
        }
        html[data-mobile-header-hidden] .event-mobile-sticky-header {
          top: 0;
        }
      `}</style>

      {/* Price Alert Modal */}
      {selectedMarket && alertModalOpen && (
        <PriceAlertModal
          isOpen={alertModalOpen}
          onClose={() => setAlertModalOpen(false)}
          marketTitle={selectedMarket.groupItemTitle || selectedMarket.question}
          marketSlug={selectedMarket.slug}
          eventSlug={paramSlug}
          marketId={selectedMarket.conditionId || selectedMarket.id}
          tokenIds={source === 'kalshi' ? [selectedMarket.slug, selectedMarket.slug] : (selectedMarket.clobTokenIds || [])}
          outcomes={selectedMarket.outcomes}
          currentPrices={selectedMarket.outcomePrices || [0.5, 0.5]}
          source={source as 'polymarket' | 'kalshi'}
          marketImage={selectedMarket.imageUrl || selectedMarket.image || eventData?.image}
        />
      )}
    </main>
  );
}
